import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: "Home",
        rooms: "Rooms",
        bidRooms: "Bid Rooms",
        secondHand: "Second Hand",
        ecommerce: "E-commerce",
        profile: "Profile",
        history: "History",
        settings: "Settings",
        dashboard: "Dashboard",
        logout: "Logout",
        login: "Login",
        management: "Management",
        adminDashboard: "Admin Dashboard",
        recoveryWallet: "Recovery Wallet",
        wallet: "Wallet",
        notifications: "Notifications",
        more: "More",
        myOffers: "My Offers",
        receivedOffers: "Received Offers",
        systemDashboard: "System Dashboard",
        analytics: "Analytics",
        preloaderTest: "Preloader Test"
      },
      
      // Hero Section
      hero: {
        slide1: {
          title: "Welcome to the Future",
          subtitle: "Experience next-generation technology",
          description: "Discover innovative solutions that transform the way you work and live.",
          cta: "Explore Now"
        },
        slide2: {
          title: "Advanced Analytics",
          subtitle: "Data-driven insights at your fingertips",
          description: "Harness the power of AI and machine learning for smarter decisions.",
          cta: "Learn More"
        },
        slide3: {
          title: "Seamless Integration",
          subtitle: "Connect everything, everywhere",
          description: "Build powerful workflows that integrate all your favorite tools.",
          cta: "Get Started"
        },
        watchDemo: "Watch Demo"
      },
      
      // Home Page
      home: {
        welcome: "Welcome back, {{name}}!",
        welcomeLabel: "Welcome Back",
        welcomeGreeting: "Hello again,",
        userFallback: "User",
        welcomeDesc: "Your digital marketplace awaits. Explore endless possibilities.",
        stats: {
          users: "Users",
          products: "Products",
          auctions: "Auctions",
          transactions: "Transactions"
        },
        quickActions: {
          title: "Quick Actions",
          subtitle: "Jump right into what you need",
          status: "All systems operational",
          ecommerce: "E-commerce",
          ecommerceDesc: "Browse products and shop",
          auctions: "Auctions",
          auctionsDesc: "Participate in live auctions",
          secondHand: "Second Hand",
          secondHandDesc: "Buy and sell used items",
          dashboard: "Dashboard",
          dashboardDesc: "Manage your account",
          ctaHint: "Start"
        },
        features: {
          title: "Why Choose Us",
          subtitle: "Experience the future of digital commerce",
          realTime: "Real-time Updates",
          realTimeDesc: "Get instant notifications and live updates",
          secure: "Secure Transactions",
          secureDesc: "Bank-level security for all transactions",
          mobile: "Mobile Optimized",
          mobileDesc: "Perfect experience on any device",
          support: "24/7 Support",
          supportDesc: "Round-the-clock customer support"
        },
        featuredRooms: {
          title: "Featured Auction Rooms",
          subtitle: "Discover premium auctions with exclusive items",
          viewAll: "View All Auction Rooms"
        },
        lastMinute: {
          title: "Last Minute Deals",
          subtitle: "Auctions ending soon - don't miss out!"
        },
        recentProducts: {
          title: "Recent Products",
          subtitle: "Fresh arrivals in our marketplace",
          viewAll: "View All Products"
        },
        secondHand: {
          title: "Second Hand Treasures",
          subtitle: "Quality pre-owned items at great prices",
          viewAll: "View All Second Hand Items"
        },
        secretBids: {
          title: "Secret Bids",
          subtitle: "Hidden auctions for the adventurous"
        },
        cta: {
          title: "Ready to Start Your Journey?",
          description: "Join thousands of users who are already experiencing the future of digital commerce.",
          getStarted: "Get Started",
          goToDashboard: "Go to Dashboard"
        },
        errors: {
          userData: "Failed to load user data. Please try again.",
          stats: "Failed to load platform stats.",
          featured: "Failed to load featured rooms.",
          lastMinute: "Failed to load last minute rooms.",
          recentProducts: "Failed to load recent products.",
          secondHand: "Failed to load second hand products.",
          secretBids: "Failed to load secret bids."
        },
        hero: {
          badge: "Tunisia's #1 Auction Marketplace",
          title1: "Win Big.",
          title2: "Buy Smart.",
          title3: "Sell Easy.",
          subtitle: "Join live auctions, discover great deals in our shop, or list your own items on Tunisia's most trusted marketplace.",
          browseAuctions: "Browse Auctions",
          shopNow: "Shop Now",
          sellItem: "Sell an Item",
          comingSoon: "Auctions starting soon",
          comingSoonDesc: "New rooms open every day. Be the first to bid.",
          exploreRooms: "Explore Rooms"
        },
        members: "Members",
        auctionsRun: "Auctions run",
        productsListed: "Products listed",
        featuredOverline: "Featured Rooms",
        featuredEmpty: "No auctions scheduled yet — check back soon!",
        browseAllRooms: "Browse all rooms",
        endingSoon: {
          overline: "Time-sensitive",
          title: "Ending Soon",
          viewAll: "All active rooms",
          currentBid: "Current bid",
          startsBid: "starts"
        },
        recentOverline: "Online Shop",
        newArrivals: "New Arrivals",
        noProducts: "No products listed yet.",
        goToShop: "Go to shop",
        howItWorks: {
          overline: "Get started in minutes",
          title: "How It Works",
          step1Title: "Create your account",
          step1Desc: "Sign up for free in under a minute. Verify your email and you're ready to go.",
          step2Title: "Browse & place bids",
          step2Desc: "Discover live auction rooms, shop products, or find second-hand treasures.",
          step3Title: "Win & receive your order",
          step3Desc: "Secure checkout via our escrow system. Your item ships once the deal is confirmed.",
          trustEscrow: "Secure escrow payments",
          trustRealtime: "Real-time bidding",
          trustCommunity: "Active community",
          trustWinners: "Verified winners"
        },
        ctaHeadline: "Ready to start bidding?",
        ctaSubtext: "Join thousands of Tunisian buyers and sellers already on Vitria. Registration is free and takes 60 seconds.",
        createAccount: "Create free account",
        browseWithout: "Browse without account",
        footer: {
          copyright: "© {{year}} Vitria. All rights reserved.",
          tagline: "Tunisia's premier auction and e-commerce marketplace. Bid live, shop smart, sell with confidence.",
          marketplace: "Marketplace",
          liveAuctions: "Live Auctions",
          onlineShop: "Online Shop",
          secondHand: "Second-hand",
          myDashboard: "My Dashboard",
          account: "Account",
          signUp: "Sign up",
          logIn: "Log in",
          profile: "Profile",
          settings: "Settings",
          support: "Support",
          helpCenter: "Help Center",
          contactUs: "Contact us",
          terms: "Terms of use",
          privacy: "Privacy policy",
          contactTitle: "Contact",
          madeWith: "Made with ❤️ in Tunisia"
        }
      },
      
      ecommerce: {
        title: "Marketplace",
        subtitle: "Discover curated launches and auction-ready inventory with a fresh, data-rich experience.",
        search: {
          placeholder: "What are you looking for?"
        },
        filter: {
          title: "Browse by category",
          subtitle: "Tap to filter the catalogue"
        },
        categories: {
          defaultHelper: "Filter by category",
          all: {
            label: "All categories",
            helper: "Show everything"
          },
          realestate: {
            label: "Real Estate",
            helper: "Filter by category"
          },
          property: {
            label: "Property",
            helper: "Filter by category"
          },
          vehicles: {
            label: "Vehicles",
            helper: "Filter by category"
          },
          electronicstech: {
            label: "Electronics & Tech",
            helper: "Filter by category"
          },
          homegarden: {
            label: "Home & Garden",
            helper: "Filter by category"
          },
          entertainment: {
            label: "Entertainment",
            helper: "Filter by category"
          },
          fashionbeauty: {
            label: "Fashion & Beauty",
            helper: "Filter by category"
          },
          jobsservices: {
            label: "Jobs & Services",
            helper: "Filter by category"
          },
          business: {
            label: "Business",
            helper: "Filter by category"
          },
          other: {
            label: "Other",
            helper: "Filter by category"
          }
        },
        product: {
          soldOut: "Sold out",
          auction: "Auction",
          unnamed: "Unnamed product",
          genericCategory: "General",
          imageAlt: "Product image",
          noDescription: "No description available",
          priceLabel: "Price",
          reservationStatus: "Reservation status",
          percentFull: "{{percent}}% full",
          statusFull: "All spots confirmed",
          statusFast: "Filling fast - reserve now",
          statusAvailable: "Spots available",
          participationFeeLabel: "Participation fee",
          participationFeeTokens: "{{count}} TND",
          unavailable: "Unavailable",
          buyNow: "Buy now",
          reserve: "Reserve spot",
          reserved: "Reserved",
          reserving: "Reserving...",
          reserveTooltip: {
            full: "All reservation spots are taken",
            owned: "You already reserved a spot",
            default: "Reserve your auction seat"
          },
          startingBid: "Starting bid: {{amount}}"
        },
        empty: {
          title: "No products found",
          subtitle: "Try adjusting the filters or check back soon for fresh inventory drops."
        },
        dialog: {
          title: "Reserve auction spot",
          infoMessage: "Secure your place before the room fills up. Payment is only required when the auction reaches full capacity.",
          totalReservations: "Total reservations",
          spotsRemaining: "{{count}} spots remaining out of {{cap}}",
          confirm: "Confirm reservation"
        },
        reservationResult: {
          full: "Auction confirmed! You secured the final spot.",
          success: "Reservation successful. We will notify you as the auction fills.",
          errorDefault: "Failed to create reservation.",
          errorCatch: "Failed to create reservation. Please try again."
        },
        errors: {
          invalidResponse: "Invalid API response structure",
          loadProducts: "Failed to load products. Please try again later."
        }
      },

      // Common
      common: {
        loading: "loading...4",
        bidders: "Bidders",
        currentBid: "Current Bid",
        endsIn: "Ends In",
        placeBid: "Place Bid",
        urgent: "URGENT",
        bidNow: "Bid Now",
        buyNow: "Buy Now",
        viewDetails: "View Details",
        secret: "SECRET",
        startingPrice: "Starting Price",
        participants: "Participants",
        joinSecret: "Join Secret",
        learnMore: "Learn More",
        retry: "Retry",
        noData: "No data found",
        close: "Close"
      },

      navbar: {
        manageProducts: "Products",
        manageAuctionRooms: "Auction Rooms",
        markAllRead: "Mark all read",
        noNotifications: "No notifications yet",
        yourWallet: "Your wallet",
  mainTokens: "Main TND",
        quickSettings: "Quick settings",
        menu: "Menu",
  tokensChip: "{{count}} TND",
        recoveryChip: "{{count}} recovery",
        quickLinks: "Quick links",
        claimCoupon: "Claim Coupon",
        guest: "Guest"
      },
      liveAuction: {
        loading: {
          title: "Connecting to the auction room...",
          description: "Please wait while we sync the live auction data."
        },
        connection: {
          refreshTooltip: "Refresh connection",
          labels: {
            connected: "Connected",
            connecting: "Connecting...",
            disconnected: "Disconnected",
            guest: "Read-only preview"
          }
        },
        alerts: {
          reconnecting: "Reconnecting..."
        },
        errors: {
          outbidWithAmount: "You have been outbid! New highest bid: {{amount}}",
          outbid: "You have been outbid! Place a higher bid to reclaim the lead.",
          observerCannotBid: "Observer mode: bidding is disabled.",
          cannotCalculateNextBid: "Unable to calculate the next bid.",
          alreadyLeading: "You are already the highest bidder.",
          observerCannotBuyPacks: "Pack purchases are reserved for participants.",
          insufficientFundsRedirect: "Insufficient TND. Redirecting to the payment page...",
          packPurchaseFailed: "Unable to purchase the pack.",
          authRequired: "Authentication required.",
          connectionLost: "Connection lost. Trying to reconnect...",
          connectionFailed: "Unable to connect to the auction room.",
          authFailed: "Authentication failed: {{message}}",
          roomLoadFailed: "Unable to load this auction room. Please try again."
        },
        success: {
          packPurchased: "Pack #{{index}} purchased"
        },
        fallback: {
          productCategory: "Auction product",
          product: "Auction product",
          room: "Auction room"
        },
        mode: {
          observer: "Observer mode",
          participant: "Participant mode",
          guest: "Guest preview"
        },
        observerNotice: "You are watching this room live. Use the \"Join\" button from the room list to participate.",
        guest: {
          title: "You are watching in read-only mode",
          description: "Room details and prices stay updated. Sign in to pay the participation fee, buy bid packs, or place bids.",
          signIn: "Sign in",
          signInToBid: "Sign in to bid",
          signInTooltip: "Sign in to participate in this auction",
          bidCaption: "Guests can follow the auction, but only signed-in participants can place bids.",
          currentPriceCaption: "Public live price — sign in to participate.",
          refreshTooltip: "Refresh room data",
          refreshed: "Room data refreshed"
        },
        countdown: {
          label: "Time remaining",
          ended: "Auction ended",
          startsIn: "Starts in",
          starting: "Starting now",
          daysHours: "{{days}}d {{hours}}h",
          hoursMinutes: "{{hours}}h {{minutes}}m",
          minutesSeconds: "{{minutes}}m {{seconds}}s"
        },
        highestBid: {
          label: "current price",
          leader: "You're leading!",
          trailing: "Stay alert and bid strategically."
        },
        quickBid: {
          buttonWithAmount: "Next bid • {{amount}}",
          button: "Next bid",
          caption: "The border shrinks as time passes. Bids placed near the buzzer reset the countdown.",
          tooltip: {
            observer: "Observer mode: bidding is disabled",
            waiting: "Waiting for room data",
            leading: "You are already leading",
            default: "Place the next minimum bid instantly"
          }
        },
        packs: {
          title: "Bid Packs",
          summary: {
            uniform: "Each pack grants {{count}} bids",
            variable: "Bid counts vary by pack",
            fallback: "Each pack grants {{count}} bids"
          },
          infiniteBids: "∞ bids",
          bidsCount: "{{count}} bids",
          remainingInfinite: "Unlimited",
          remaining: "{{count}} remaining",
          tooltips: {
            purchase: "Tap to buy this pack",
            owned: "Pack already owned",
            locked: "Unlock the previous pack first"
          },
          actions: {
            buyShort: "Buy",
            locked: "Locked",
            buyFull: "Buy pack",
            waitForPrevious: "Wait for pack #{{index}}"
          },
          status: {
            exhausted: "Used up",
            current: "Active"
          },
          usageSummary: "{{used}}/{{total}} used",
          cardLabel: "Pack #{{index}}",
          footnote: "💡 Packs 1 to 5 offer fixed bid bundles. After Pack 5, you pay per bid. Unused bids from packs are refunded if you lose; used bids convert into recovery value."
        },
        dialogs: {
          participation: {
            title: "Confirm participation fee",
            description: "Joining as a participant immediately deducts the participation fee from your wallet. Continue only if you agree to pay now.",
            roomLabel: "Auction room",
            feeLabel: "Participation fee",
            walletBalance: "Wallet balance",
            insufficientFunds: "You do not have enough TND to cover the participation fee. Please top up your wallet before joining.",
            loading: "Fetching auction details...",
            buttons: {
              viewAsObserver: "Watch as observer",
              payAndJoin: "Pay and join",
              join: "Join as participant"
            }
          },
          packPurchase: {
            title: "Buy bid pack #{{index}}",
            description: "This pack is required to continue bidding. Each pack unlocks the next tier. Pack 5 offers a fixed bid bundle.",
            priceLabel: "Price",
            autoPurchase: "Automatically buy the next pack when needed",
            buttons: {
              cancel: "Cancel",
              processing: "Processing...",
              confirm: "Buy pack"
            }
          }
        },
        common: {
          amountWithCurrency: "{{amount}} TND",
          currency: "TND"
        },
        winnerModal: {
          title: "Congratulations!",
          subtitle: "You won the auction 🎉",
          winnerLabel: "Winner",
          bidLabel: "Winning bid",
          actions: {
            viewDashboard: "View dashboard",
            keepBidding: "Keep bidding"
          }
        },
        notifications: {
          observerMode: "Observer mode enabled - enjoy the live action!",
          joined: "Successfully joined the auction room!",
          bidPlaced: "Bid placed!",
          auctionExtended: "⏰ Auction extended! New end time: {{time}}",
          auctionEnded: "🏁 The auction has ended!",
          youWon: "🎉 Congratulations! You won the auction! 🎉",
          noWinner: "The auction ended without a winner."
        }
      },
      
      // Dashboard
      installationSystemStatus: "Installation System Status",
      monitorSystemDuringInstallation: "Monitor your system during installation and setup",
      
      // Common
      welcome: "Welcome",
      login: "Login",
      register: "Register",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm Password",
      forgotPassword: "Forgot Password?",
      resetPassword: "Reset Password",
      submit: "Submit",
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      loading: "loading...4",
      error: "Error",
      success: "Success",
      
      // Error pages
      pageNotFound: "Page Not Found",
      pageNotFoundDescription: "The page you're looking for doesn't exist.",
      goToLogin: "Go to Login",
      
      // History page
      history: {
        title: "History",
        subtitle: "View your bidding history and purchase records"
      },

      // Received Offers page
      receivedOffers: {
        title: "Received Offers",
        subtitle: "Review and respond to offers on your second-hand products"
      },

      // My Offers page
      myOffers: {
        title: "My Offers",
        subtitle: "Track the status of offers you've made on second-hand products"
      },

      // Unauthorized page
      unauthorized: {
        accessDenied: "Access Denied",
        noPermission: "You don't have permission to access this page.",
        currentAccessLevel: "Your Current Access Level",
        role: "Role",
        roleNotAssigned: "Not assigned",
        permissions: "Permissions",
        permissionsNone: "None",
        goToHome: "Go to Home",
        viewProfile: "View Profile",
        contactAdmin: "If you believe this is an error, please contact an administrator."
      },

      // Auth page
      auth: {
        realTimeSecured: "Real-time auctions, secured",
        brandName: "Vitria",
        tagline: "Bid, sell, and manage rooms without friction.",
        loginDescription: "Log in to access live rooms, wallet balances, and your offers dashboard.",
        settlement: "Settlement",
        settlementDesc: "TND escrow support",
        coverage: "Coverage",
        coverageDesc: "Auctions & resale rooms",
        otpNotice: "Please check your email for a verification code to activate your account.",
        emailVerified: "Email verified! You can now log in.",
        resendCodeMessage: "Please check your email for a new code.",
        resendCodeNotice: "A new verification code has been sent to your email.",
        verifyEmailNotice: "Please verify your email. Enter the code sent to your email below."
      },

      // Notifications page
      notifications: {
        title: "Notifications",
        unread: "{{count}} unread",
        markAllRead: "Mark All Read",
        deleteAll: "Delete All",
        deleteAllConfirm: "Delete all notifications?",
        noNotifications: "No notifications.",
        markRead: "Mark read",
        loadMore: "Load More",
        newNotification: "New Notification"
      },

      // Chat page
      chat: {
        contacts: "Contacts",
        noChatsYet: "No unlocked chats yet.",
        product: "Product: {{name}}",
        offer: "Offer: {{amount}}",
        chatAbout: "Chat about: {{name}}",
        noMessages: "No messages yet. Say hello!",
        you: "You",
        partner: "Partner",
        typePlaceholder: "Type a message",
        send: "Send",
        selectContact: "Select a contact to start chatting.",
        generalChat: "General chat",
        unknownProduct: "Unknown product"
      },

      // Profile page
      profile: {
        dashboard: "Profile Dashboard",
        subtitle: "Manage your account information and preferences",
        memberSince: "Member since {{year}}",
        history: "History",
        bidsAndPurchases: "Bids & purchases",
        myOffers: "My Offers",
        secondHandOffers: "Second-hand offers",
        received: "Received",
        offersOnListings: "Offers on listings",
        personalInfo: "Personal Information",
        editProfile: "Edit Profile",
        firstName: "First Name",
        lastName: "Last Name",
        username: "Username",
        emailAddress: "Email Address",
        role: "Role",
        accountStatus: "Account Status",
        active: "Active"
      },

      // Product Details page
      productDetails: {
        backToMarketplace: "Back to Marketplace",
        productNotFound: "Product not found.",
        fetchError: "Failed to fetch product details.",
        soldOut: "SOLD OUT",
        outOfStock: "Out of Stock",
        reviewCount: "({{count}} reviews)",
        quantity: "Quantity",
        unavailable: "Unavailable",
        buyNow: "Buy Now",
        secureTransaction: "Secure transaction \u2022 Verified Seller \u2022 Fast Delivery",
        customerReviews: "Customer Reviews",
        noReviews: "No reviews yet. Be the first to review!",
        userFallback: "User",
        writeReview: "Write a Review",
        rating: "Rating",
        ratingExcellent: "5 - Excellent",
        ratingGood: "4 - Good",
        ratingAverage: "3 - Average",
        ratingPoor: "2 - Poor",
        ratingTerrible: "1 - Terrible",
        yourReview: "Your Review",
        sharePlaceholder: "Share your experience...",
        submitting: "Submitting...",
        submitReview: "Submit Review",
        verifiedBuyersOnly: "Only verified buyers can leave reviews.",
        loginToReview: "Log in and purchase this product to leave a review."
      },

      // Offer Details page
      offerDetails: {
        title: "Offer Details",
        failedToLoad: "Failed to load offer details",
        payFeeConfirm: "Pay {{amount}} TND to unlock chat?",
        feePaidSuccess: "Fee paid successfully! Chat is now unlocked.",
        failedToPayFee: "Failed to pay fee",
        offerActionSuccess: "Offer {{action}}ed successfully!",
        offerActionFailed: "Failed to {{action}} offer",
        withdrawConfirm: "Are you sure you want to withdraw this offer?",
        withdrawSuccess: "Offer withdrawn successfully!",
        failedToWithdraw: "Failed to withdraw offer",
        pleaseLogin: "Please Login",
        loginRequired: "You need to be logged in to view offer details.",
        backToMyOffers: "Back to My Offers",
        backToReceivedOffers: "Back to Received Offers",
        offerNotFound: "Offer Not Found",
        offerNotFoundDesc: "The offer you're looking for doesn't exist.",
        yourOfferOn: "Your offer on",
        offerReceivedFor: "Offer received for",
        reservePrice: "Reserve Price",
        condition: "Condition",
        category: "Category",
        yourOffer: "Your Offer",
        status: "Status",
        timeLeft: "Time Left",
        pendingActivation: "Pending activation",
        expired: "Expired",
        seller: "Seller",
        buyer: "Buyer",
        date: "Date",
        message: "Message",
        sellerResponseLabel: "Seller Response",
        actions: "Actions",
        chatUnlocked: "Chat Unlocked!",
        sellerChatMsg: "The buyer has completed the fee. Start chatting to finalize the sale.",
        buyerChatMsg: "You can now contact the seller to finalize the deal.",
        startChat: "💬 Start Chat",
        sellerAwaitingFee: "⏳ Waiting for the buyer to pay the chat fee.",
        buyerAwaitingFee: "⏳ The seller accepted your offer. Pay the chat fee to unlock conversation.",
        processing: "Processing...",
        payFeeButton: "💰 Pay {{amount}} TND fee",
        optionalResponsePlaceholder: "Optional response message...",
        accept: "✅ Accept",
        reject: "❌ Reject",
        buyerNotifiedFee: "The buyer has been notified to pay the chat fee. You will be notified once it is completed.",
        withdrawOffer: "🚫 Withdraw Offer",
        acceptOfferTitle: "Accept Offer & Unlock Chat",
        acceptOfferDesc: "Confirm acceptance and choose how long the buyer has access before the offer expires.",
        productLabel: "Product",
        offerAmountLabel: "Offer Amount",
        chatFeeLabel: "Chat Fee",
        foreverOption: "Forever (No Expiration)",
        foreverDesc: "Offer stays active until you manually close it.",
        countdownOption: "Countdown",
        countdownDesc: "Expires after the selected number of hours (max 720).",
        hours: "hours",
        provideDuration: "Provide a duration or switch to forever.",
        maxDuration: "Maximum allowed duration is 720 hours (30 days).",
        confirmAcceptance: "Confirm Acceptance"
      },

      // Rooms Bid page
      roomsBid: {
        title: "Auction Rooms",
        subtitle: "Choose to participate and bid with your tokens, or observe the live action for free.",
        free: "Free",
        noDeletePermission: "You do not have permission to delete rooms.",
        deleteConfirm: "Are you sure you want to delete this auction room?",
        deleteSuccess: "Auction room deleted successfully",
        deleteFailed: "Failed to delete auction room",
        deleteError: "Error deleting auction room",
        untitledRoom: "Untitled room",
        live: "Live",
        scheduled: "Scheduled",
        descriptionComingSoon: "Description coming soon.",
        lastBid: "Latest bid",
        timeRemaining: "Time remaining",
        participationFee: "Participation fee: {{fee}}",
        participate: "Participate",
        signInToParticipate: "Sign in to participate",
        view: "View",
        startingBid: "Starting bid",
        startsIn: "Starts in",
        notYetAvailable: "Not yet available",
        notStartedTooltip: "This auction room has not started yet",
        comingSoon: "Coming soon",
        createRoom: "Create a Room",
        activeAuctions: "Active auctions",
        upcomingRooms: "Upcoming rooms",
        noRoomsTitle: "No auction rooms available",
        noRoomsDesc: "There are no live or scheduled rooms right now. Check back soon or create a new auction room to kickstart the action.",
        autoRefreshNotice: "Stay tuned\u2014rooms refresh automatically every 30 seconds.",
        guestBanner: {
          title: "Public auction preview",
          description: "Browse live and scheduled rooms freely. Sign in when you are ready to participate or bid.",
          action: "Sign in"
        }
      },

      // Auction Room Management page
      auctionManagement: {
        accessDenied: "Access Denied",
        noPermission: "You don't have permission to manage auction rooms.",
        title: "Auction Room Management",
        createRoom: "Create Auction Room",
        unknownProduct: "Unknown Product",
        unknownUser: "Unknown user",
        minBid: "Min Bid:",
        bidIncrement: "Bid Increment:",
        currentBid: "Current Bid:",
        noBid: "None",
        participants: "Participants:",
        start: "Start:",
        end: "End:",
        mode: "Mode:",
        history: {
          title: "Auction Room History",
          subtitle: "Review closed or cancelled rooms and inspect their bid timelines.",
          closed: "Closed",
          cancelled: "Cancelled",
          recentRooms: "Recent Rooms",
          noRooms: "No rooms found for this status yet.",
          participants: "participants",
          finalBid: "Final Bid",
          noCategory: "No category",
          finalPrice: "Final Price",
          startTime: "Start Time",
          endTime: "End Time",
          duration: "Duration",
          winner: "Winner",
          noWinner: "No winner",
          participationFee: "Participation Fee",
          minBidIncrement: "Min Bid / Increment",
          bidHistory: "Bid History",
          refreshing: "Refreshing\u2026",
          bidder: "Bidder",
          amount: "Amount",
          time: "Time",
          auto: "Auto",
          noBids: "No bids were recorded for this room.",
          selectRoom: "Select a room to view its history."
        },
        form: {
          editTitle: "Edit Auction Room",
          createTitle: "Create Auction Room",
          product: "Product",
          selectProduct: "Select Product",
          reservableOnly: "Only reservable products can be used to create auction rooms.",
          feeApplied: "Participation fee applied: {{fee}}",
          feeFromProduct: "This value comes directly from the product configuration.",
          participationFee: "Participation Fee (TND)",
          selectProductFirst: "Select a product first",
          feeHelp: "This fee is pulled directly from the selected product. Update the product to change it.",
          minimumBid: "Minimum Bid (TND)",
          bidIncrement: "Bid Increment (TND)",
          startTime: "Start Time",
          durationMinutes: "Duration (minutes)",
          computedEndTime: "Computed End Time",
          packIncrement: "Pack Increment (bids per pack)",
          derivedServerSide: "Derived server-side",
          mode: "Mode",
          fixed: "Fixed",
          autoExtend: "Auto-Extend",
          status: "Status",
          pending: "Pending",
          active: "Active",
          closed: "Closed",
          cancelled: "Cancelled",
          autoExtendDuration: "Auto-Extend Duration (seconds)",
          triggerThreshold: "Trigger Threshold (seconds remaining)",
          update: "Update Auction Room",
          create: "Create Auction Room"
        },
        errors: {
          startTimeRequired: "Start time and duration are required",
          durationPositive: "Duration must be a positive number of minutes",
          endAfterStart: "End time must be after start time",
          saveFailed: "Error saving auction room",
          deleteConfirm: "Are you sure you want to delete this auction room?",
          deleteFailed: "Error deleting auction room"
        }
      },

      // Second Hand Rooms page
      roomsSecondHand: {
        title: "Second-hand bid rooms",
        subtitle: "Discover pre-loved treasures with curated auction rooms. Track availability, reserve prices, and engage sellers directly with secure offers.",
        requestRoom: "Request auction room",
        status: {
          active: "Live",
          pending: "Pending approval",
          expired: "Expired",
          sold: "Sold",
          closed: "Closed",
          canceled: "Closed",
          scheduled: "Scheduled"
        },
        condition: {
          like_new: "Like new",
          excellent: "Excellent",
          good: "Good",
          used: "Used",
          fair: "Fair",
          poor: "Poor"
        },
        duration: {
          dayLeft: "1 day left",
          daysLeft: "{{count}} days left",
          daysHoursLeft: "{{days}}d {{hours}}h left",
          hoursMinutesLeft: "{{hours}}h {{minutes}}m left"
        },
        card: {
          unnamed: "Unnamed product",
          general: "General",
          noDescription: "No description provided.",
          reservePrice: "Asking price",
          openToOffers: "Open to offers",
          duration: "Duration",
          condition: "Condition",
          seller: "Seller",
          anonymous: "Anonymous",
          makeOffer: "Make offer"
        },
        empty: {
          title: "No items match this filter",
          subtitle: "Check back soon; new requests land every few minutes once sellers submit their rooms."
        },
        sellerForm: {
          title: "Request auction room",
          reviewNotice: "Your request is reviewed by an administrator. Once approved, the auction room will go live using the duration rules you define below.",
          tndNotice: "TND holds protect both parties. Review the rules before submitting your listing.",
          viewRules: "View TND hold rules",
          productName: "Product name",
          category: "Category",
          selectCategory: "Select category",
          description: "Description",
          reservePrice: "Asking price (TND)",
          reservePriceHelp: "Optional — leave blank to invite any offer",
          condition: "Condition",
          uploadImages: "Upload images",
          uploadHelp: "Upload up to 5 images (max 10 MB each).",
          filesSelected: "{{count}} file(s) selected",
          listingVisibility: "Listing visibility",
          listingVisibilityHelp: "Choose how long accepted offers remain valid by default. Selecting \"Infinite\" hides the duration on listings.",
          infinite: "Infinite (\u221e)",
          countdown: "Countdown",
          defaultDuration: "Default duration (hours)",
          maxDuration: "Maximum of 720 hours (30 days).",
          submitting: "Submitting\u2026",
          submitRequest: "Submit request"
        },
        offerModal: {
          title: "Make an offer",
          reservePrice: "Reserve price: {{price}}",
          lockNotice: "Placing an offer temporarily locks 1 TND until the seller responds.",
          viewRules: "View TND hold rules",
          offerLabel: "Your offer (TND)",
          submitting: "Submitting\u2026",
          submitOffer: "Submit offer"
        },
        toasts: {
          loginRequired: "Please login to make an offer.",
          ownProduct: "You cannot make an offer on your own product.",
          invalidAmount: "Please enter a valid offer amount.",
          offerSuccess: "Offer submitted successfully! The seller will review your offer.",
          offerFailed: "Failed to submit offer.",
          offerFailedRetry: "Failed to submit offer. Please try again.",
          fillRequired: "Please fill in all required fields.",
          pricePositive: "Starting price must be greater than 0.",
          reserveMinimum: "Reserve price must be greater than or equal to the starting price.",
          requestSuccess: "Auction room request submitted! An admin will review your request.",
          requestFailed: "Failed to submit request.",
          requestFailedRetry: "Failed to submit request. Please try again."
        }
      },

      // Payment page
      payment: {
        noProductId: "No product ID provided",
        productNotFoundTitle: "Product Not Found",
        invalidResponse: "Invalid response format",
        nameRequired: "Name is required",
        emailRequired: "Email is required",
        cardRequired: "Card number is required",
        expiryRequired: "Expiry date is required",
        cvcRequired: "CVC is required",
        addressRequired: "Address is required",
        cityRequired: "City is required",
        postalCodeRequired: "Postal code is required",
        invalidCardLength: "Invalid card number length",
        invalidExpiryFormat: "Invalid expiry format (MM/YY)",
        invalidCvcLength: "Invalid CVC length",
        loginRequired: "Please log in to complete the purchase.",
        fixErrors: "Please fix the following errors:",
        purchaseFailed: "Purchase failed",
        paymentSuccessRedirect: "Payment successful! Order for {{name}} has been processed.",
        unexpectedResponse: "Unexpected response from server",
        paymentFailed: "Payment failed. Please try again.",
        loadingProduct: "Loading product details...",
        backToProducts: "Back to Products",
        paymentSuccess: "Payment Successful!",
        orderProcessed: "Your order for {{name}} has been processed.",
        totalPaid: "Total Paid",
        quantity: "Quantity",
        redirecting: "Redirecting to products page...",
        orderSummary: "Order Summary",
        soldOut: "Sold Out",
        subtotal: "Subtotal",
        shipping: "Shipping",
        free: "Free",
        tax: "Tax (20%)",
        total: "Total",
        paymentInfo: "Payment Information",
        creditCardDetails: "Credit Card Details",
        fullName: "Full Name",
        cardNumber: "Card Number",
        expiryDate: "Expiry Date",
        cvc: "CVC",
        billingAddress: "Billing Address",
        address: "Address",
        city: "City",
        postalCode: "Postal Code",
        payAmount: "Pay {{amount}}",
        purchaseWithWallet: "Purchase with {{wallet}}",
        recoveryWallet: "Recovery Wallet",
        mainWallet: "Main Wallet",
        completePurchaseAuto: "Complete Purchase (Auto-Optimized)"
      },

      // Theme
      darkMode: "Dark Mode",
      lightMode: "Light Mode",
      
      // Language
      language: "Language",
      english: "English",
      arabic: "العربية",
      french: "Français"
    }
  },
  ar: {
    translation: {
      // Navigation
      nav: {
        home: "الرئيسية",
        rooms: "الغرف",
        bidRooms: "غرف المزايدة",
        secondHand: "مستعمل",
        ecommerce: "التجارة الإلكترونية",
        profile: "الملف الشخصي",
        history: "التاريخ",
        settings: "الإعدادات",
        dashboard: "لوحة التحكم",
        logout: "تسجيل الخروج",
        login: "تسجيل الدخول",
        management: "الإدارة",
        adminDashboard: "لوحة تحكم المدير",
        recoveryWallet: "محفظة الاسترداد",
        wallet: "المحفظة",
        notifications: "الإشعارات",
        more: "المزيد",
        myOffers: "عروضي",
        receivedOffers: "العروض المستلمة",
        systemDashboard: "لوحة النظام",
        analytics: "التحليلات",
        preloaderTest: "اختبار شاشة التحميل"
      },
      
      // Hero Section
      hero: {
        slide1: {
          title: "أهلاً بك في المستقبل",
          subtitle: "تجربة تكنولوجيا الجيل القادم",
          description: "اكتشف الحلول المبتكرة التي تحول طريقة عملك وحياتك.",
          cta: "استكشف الآن"
        },
        slide2: {
          title: "التحليلات المتقدمة",
          subtitle: "رؤى مدفوعة بالبيانات في متناول يدك",
          description: "استفد من قوة الذكاء الاصطناعي والتعلم الآلي لاتخاذ قرارات أذكى.",
          cta: "اعرف المزيد"
        },
        slide3: {
          title: "التكامل السلس",
          subtitle: "اربط كل شيء، في كل مكان",
          description: "أنشئ تدفقات عمل قوية تدمج جميع الأدوات المفضلة لديك.",
          cta: "ابدأ الآن"
        },
        watchDemo: "شاهد العرض التوضيحي"
      },
      
      // Home Page
      home: {
        welcome: "مرحباً بعودتك، {{name}}!",
        welcomeLabel: "مرحباً بعودتك",
        welcomeGreeting: "مرحباً مجدداً،",
        userFallback: "مستخدم",
        welcomeDesc: "السوق الرقمي الخاص بك في انتظارك. استكشف الإمكانيات اللانهائية.",
        stats: {
          users: "المستخدمين",
          products: "المنتجات",
          auctions: "المزادات",
          transactions: "المعاملات"
        },
        quickActions: {
          title: "الإجراءات السريعة",
          subtitle: "انتقل مباشرة إلى ما تحتاجه",
          status: "جميع الأنظمة تعمل",
          ecommerce: "التجارة الإلكترونية",
          ecommerceDesc: "تصفح المنتجات والتسوق",
          auctions: "المزادات",
          auctionsDesc: "شارك في المزادات المباشرة",
          secondHand: "مستعمل",
          secondHandDesc: "اشترِ وبع السلع المستعملة",
          dashboard: "لوحة التحكم",
          dashboardDesc: "إدارة حسابك",
          ctaHint: "ابدأ"
        },
        features: {
          title: "لماذا تختارنا",
          subtitle: "تجربة مستقبل التجارة الرقمية",
          realTime: "التحديثات الفورية",
          realTimeDesc: "احصل على إشعارات فورية وتحديثات مباشرة",
          secure: "المعاملات الآمنة",
          secureDesc: "أمان على مستوى البنوك لجميع المعاملات",
          mobile: "محسن للهاتف المحمول",
          mobileDesc: "تجربة مثالية على أي جهاز",
          support: "الدعم على مدار الساعة",
          supportDesc: "دعم العملاء على مدار الساعة"
        },
        featuredRooms: {
          title: "غرف المزادات المميزة",
          subtitle: "اكتشف المزادات المميزة مع عناصر حصرية",
          viewAll: "عرض جميع غرف المزادات"
        },
        lastMinute: {
          title: "عروض اللحظة الأخيرة",
          subtitle: "مزادات على وشك الانتهاء - لا تفوت الفرصة!"
        },
        recentProducts: {
          title: "المنتجات الأخيرة",
          subtitle: "وصول جديدة في سوقنا",
          viewAll: "عرض جميع المنتجات"
        },
        secondHand: {
          title: "كنوز المستعمل",
          subtitle: "عناصر مستعملة ذات جودة بأسعار رائعة",
          viewAll: "عرض جميع عناصر المستعمل"
        },
        secretBids: {
          title: "المزايدات السرية",
          subtitle: "مزادات مخفية للمغامرين"
        },
        cta: {
          title: "مستعد لبدء رحلتك؟",
          description: "انضم إلى آلاف المستخدمين الذين يخوضون بالفعل تجربة مستقبل التجارة الرقمية.",
          getStarted: "ابدأ الآن",
          goToDashboard: "الذهاب إلى لوحة التحكم"
        },
        errors: {
          userData: "فشل في تحميل بيانات المستخدم. يرجى المحاولة مرة أخرى.",
          stats: "فشل في تحميل إحصائيات المنصة.",
          featured: "فشل في تحميل غرف المزاد المميزة.",
          lastMinute: "فشل في تحميل عروض اللحظة الأخيرة.",
          recentProducts: "فشل في تحميل المنتجات الحديثة.",
          secondHand: "فشل في تحميل منتجات المستعمل.",
          secretBids: "فشل في تحميل المزايدات السرية."
        },
        hero: {
          badge: "السوق رقم 1 في تونس للمزادات",
          title1: "اربح كثيراً.",
          title2: "اشترِ بذكاء.",
          title3: "بِع بسهولة.",
          subtitle: "انضم إلى المزادات المباشرة، اكتشف أفضل العروض في متجرنا، أو أدرج منتجاتك في أكثر سوق موثوق في تونس.",
          browseAuctions: "تصفح المزادات",
          shopNow: "تسوق الآن",
          sellItem: "بِع منتجاً",
          comingSoon: "مزادات قادمة قريباً",
          comingSoonDesc: "تفتح غرف جديدة كل يوم. كن أول من يزايد.",
          exploreRooms: "استكشف الغرف"
        },
        members: "الأعضاء",
        auctionsRun: "مزادات أُجريت",
        productsListed: "منتجات مدرجة",
        featuredOverline: "غرف مميزة",
        featuredEmpty: "لا توجد مزادات مجدولة بعد — تحقق قريباً!",
        browseAllRooms: "تصفح جميع الغرف",
        endingSoon: {
          overline: "حساس للوقت",
          title: "تنتهي قريباً",
          viewAll: "جميع الغرف النشطة",
          currentBid: "المزايدة الحالية",
          startsBid: "يبدأ بـ"
        },
        recentOverline: "المتجر الإلكتروني",
        newArrivals: "وصل حديثاً",
        noProducts: "لم تُدرج منتجات بعد.",
        goToShop: "الذهاب إلى المتجر",
        howItWorks: {
          overline: "ابدأ في دقائق",
          title: "كيف يعمل",
          step1Title: "أنشئ حسابك",
          step1Desc: "سجّل مجاناً في أقل من دقيقة. تحقق من بريدك الإلكتروني وأنت جاهز.",
          step2Title: "تصفح وقدّم عروضك",
          step2Desc: "اكتشف غرف المزادات المباشرة، أو تسوق المنتجات، أو ابحث عن كنوز المستعمل.",
          step3Title: "اربح واستلم طلبك",
          step3Desc: "دفع آمن عبر نظام الضمان لدينا. يُشحن عنصرك بمجرد تأكيد الصفقة.",
          trustEscrow: "مدفوعات ضمان آمنة",
          trustRealtime: "مزايدة في الوقت الفعلي",
          trustCommunity: "مجتمع نشط",
          trustWinners: "فائزون موثقون"
        },
        ctaHeadline: "هل أنت مستعد للمزايدة؟",
        ctaSubtext: "انضم إلى آلاف المشترين والبائعين التونسيين على Vitria. التسجيل مجاني ويستغرق 60 ثانية.",
        createAccount: "أنشئ حساباً مجانياً",
        browseWithout: "تصفح بدون حساب",
        footer: {
          copyright: "© {{year}} Vitria. جميع الحقوق محفوظة.",
          tagline: "السوق الأول في تونس للمزادات والتجارة الإلكترونية. زايد مباشرةً، تسوق بذكاء، بِع بثقة.",
          marketplace: "السوق",
          liveAuctions: "مزادات مباشرة",
          onlineShop: "المتجر الإلكتروني",
          secondHand: "مستعمل",
          myDashboard: "لوحة التحكم",
          account: "الحساب",
          signUp: "إنشاء حساب",
          logIn: "تسجيل الدخول",
          profile: "الملف الشخصي",
          settings: "الإعدادات",
          support: "الدعم",
          helpCenter: "مركز المساعدة",
          contactUs: "تواصل معنا",
          terms: "شروط الاستخدام",
          privacy: "سياسة الخصوصية",
          contactTitle: "التواصل",
          madeWith: "صُنع بـ ❤️ في تونس"
        }
      },
      
      ecommerce: {
        title: "السوق",
        subtitle: "اكتشف الإطلاقات المنسقة والبضائع الجاهزة للمزاد بتجربة غنية بالبيانات.",
        search: {
          placeholder: "ما الذي تبحث عنه؟"
        },
        filter: {
          title: "تصفح حسب الفئة",
          subtitle: "اضغط لتصفية الكتالوج"
        },
        categories: {
          defaultHelper: "تصفية حسب الفئة",
          all: {
            label: "جميع الفئات",
            helper: "اعرض كل العناصر"
          },
          realestate: {
            label: "العقارات",
            helper: "تصفية حسب الفئة"
          },
          property: {
            label: "الأملاك",
            helper: "تصفية حسب الفئة"
          },
          vehicles: {
            label: "المركبات",
            helper: "تصفية حسب الفئة"
          },
          electronicstech: {
            label: "الإلكترونيات والتقنية",
            helper: "تصفية حسب الفئة"
          },
          homegarden: {
            label: "المنزل والحديقة",
            helper: "تصفية حسب الفئة"
          },
          entertainment: {
            label: "الترفيه",
            helper: "تصفية حسب الفئة"
          },
          fashionbeauty: {
            label: "الأزياء والجمال",
            helper: "تصفية حسب الفئة"
          },
          jobsservices: {
            label: "الوظائف والخدمات",
            helper: "تصفية حسب الفئة"
          },
          business: {
            label: "الأعمال",
            helper: "تصفية حسب الفئة"
          },
          other: {
            label: "فئات أخرى",
            helper: "تصفية حسب الفئة"
          }
        },
        product: {
          soldOut: "غير متوفر",
          auction: "مزاد",
          unnamed: "منتج دون اسم",
          genericCategory: "عام",
          imageAlt: "صورة المنتج",
          noDescription: "لا يوجد وصف متاح",
          priceLabel: "السعر",
          reservationStatus: "حالة الحجز",
          percentFull: "{{percent}}% مكتمل",
          statusFull: "تم تأكيد جميع المقاعد",
          statusFast: "المقاعد تمتلئ بسرعة - احجز الآن",
          statusAvailable: "مقاعد متاحة",
          participationFeeLabel: "رسوم المشاركة",
          participationFeeTokens: "{{count}} TND",
          unavailable: "غير متاح",
          buyNow: "اشتر الآن",
          reserve: "احجز مقعدًا",
          reserved: "تم الحجز",
          reserving: "جارٍ الحجز...",
          reserveTooltip: {
            full: "تم حجز جميع المقاعد",
            owned: "لديك حجز مسبقًا",
            default: "احجز مقعدك في المزاد"
          },
          startingBid: "سعر البدء: {{amount}}"
        },
        empty: {
          title: "لم يتم العثور على منتجات",
          subtitle: "جرّب تعديل عوامل التصفية أو عد لاحقًا للاطلاع على أحدث الإصدارات."
        },
        dialog: {
          title: "حجز مقعد في المزاد",
          infoMessage: "أمّن مقعدك قبل أن تمتلئ القاعة. لن يتم فرض الدفع إلا عند اكتمال السعة.",
          totalReservations: "إجمالي الحجوزات",
          spotsRemaining: "{{count}} مقاعد متبقية من أصل {{cap}}",
          confirm: "تأكيد الحجز"
        },
        reservationResult: {
          full: "تم تأكيد المزاد! لقد حصلت على المقعد الأخير.",
          success: "تم الحجز بنجاح. سنقوم بإعلامك عند اقتراب اكتمال المزاد.",
          errorDefault: "فشل إنشاء الحجز.",
          errorCatch: "تعذّر إنشاء الحجز. حاول مرة أخرى."
        },
        errors: {
          invalidResponse: "استجابة واجهة البرمجة غير صالحة",
          loadProducts: "فشل تحميل المنتجات. يرجى المحاولة لاحقًا."
        }
      },

      // Common
      common: {
        loading: "جاري التحميل...",
        bidders: "المزايدون",
        currentBid: "أعلى عرض",
        endsIn: "ينتهي في",
        placeBid: "قدم عرض",
        urgent: "عاجل",
        bidNow: "قدم عرض الآن",
        buyNow: "اشترِ الآن",
        viewDetails: "عرض التفاصيل",
        secret: "سري",
        startingPrice: "سعر البدء",
        participants: "المشاركون",
        joinSecret: "انضم إلى السري",
        learnMore: "اعرف المزيد",
        retry: "إعادة المحاولة",
        noData: "لا توجد بيانات",
        close: "إغلاق"
      },

      navbar: {
        manageProducts: "المنتجات",
        manageAuctionRooms: "غرف المزاد",
        markAllRead: "تحديد الكل كمقروء",
        noNotifications: "لا توجد إشعارات بعد",
        yourWallet: "محفظتك",
  mainTokens: "الرصيد الرئيسي (TND)",
        quickSettings: "إعدادات سريعة",
        menu: "القائمة",
  tokensChip: "{{count}} TND",
        recoveryChip: "{{count}} استرداد",
        quickLinks: "روابط سريعة",
        claimCoupon: "مطالبة بقسيمة",
        guest: "ضيف"
      },
      liveAuction: {
        loading: {
          title: "جارٍ الاتصال بغرفة المزاد...",
          description: "يرجى الانتظار بينما نقوم بمزامنة بيانات المزاد المباشر."
        },
        connection: {
          refreshTooltip: "تحديث الاتصال",
          labels: {
            connected: "متصل",
            connecting: "جارٍ الاتصال...",
            disconnected: "غير متصل",
            guest: "معاينة للقراءة فقط"
          }
        },
        alerts: {
          reconnecting: "جارٍ إعادة الاتصال..."
        },
        errors: {
          outbidWithAmount: "تمت المزايدة عليك! العرض الأعلى الجديد: {{amount}}",
          outbid: "تمت المزايدة عليك! قدم عرضاً أعلى لاستعادة الصدارة.",
          observerCannotBid: "وضع المراقب: المزايدة غير متاحة.",
          cannotCalculateNextBid: "تعذر حساب العرض التالي.",
          alreadyLeading: "أنت بالفعل صاحب أعلى عرض.",
          observerCannotBuyPacks: "شراء الباقات متاح للمشاركين فقط.",
          insufficientFundsRedirect: "الرصيد غير كافٍ. سيتم تحويلك إلى صفحة الدفع...",
          packPurchaseFailed: "تعذر شراء الباقة.",
          authRequired: "المصادقة مطلوبة.",
          connectionLost: "انقطع الاتصال. جارٍ محاولة إعادة الاتصال...",
          connectionFailed: "تعذر الاتصال بغرفة المزاد.",
          authFailed: "فشلت المصادقة: {{message}}",
          roomLoadFailed: "تعذر تحميل غرفة المزاد. يرجى المحاولة مرة أخرى."
        },
        success: {
          packPurchased: "تم شراء الباقة رقم {{index}}"
        },
        fallback: {
          productCategory: "منتج المزاد",
          product: "منتج المزاد",
          room: "غرفة المزاد"
        },
        mode: {
          observer: "وضع المراقب",
          participant: "وضع المشارك",
          guest: "معاينة الزائر"
        },
        observerNotice: "أنت تتابع هذه الغرفة مباشرة. استخدم زر \"انضمام\" من قائمة الغرف للمشاركة.",
        guest: {
          title: "أنت تشاهد في وضع القراءة فقط",
          description: "يتم تحديث تفاصيل الغرفة والأسعار تلقائيًا. سجّل الدخول لدفع رسوم المشاركة أو شراء الباقات أو المزايدة.",
          signIn: "تسجيل الدخول",
          signInToBid: "سجّل الدخول للمزايدة",
          signInTooltip: "سجّل الدخول للمشاركة في هذا المزاد",
          bidCaption: "يمكن للزوار متابعة المزاد، لكن المزايدة متاحة فقط للمشاركين المسجلين.",
          currentPriceCaption: "السعر المباشر متاح للجميع — سجّل الدخول للمشاركة.",
          refreshTooltip: "تحديث بيانات الغرفة",
          refreshed: "تم تحديث بيانات الغرفة"
        },
        countdown: {
          label: "الوقت المتبقي",
          ended: "انتهى المزاد",
          startsIn: "يبدأ خلال",
          starting: "يبدأ الآن",
          daysHours: "{{days}} يوم {{hours}} س",
          hoursMinutes: "{{hours}} س {{minutes}} د",
          minutesSeconds: "{{minutes}} د {{seconds}} ث"
        },
        highestBid: {
          label: "أعلى عرض",
          leader: "أنت في الصدارة!",
          trailing: "ابق متيقظاً وقدم عروضك بذكاء."
        },
        quickBid: {
          buttonWithAmount: "العرض التالي • {{amount}}",
          button: "العرض التالي",
          caption: "يتناقص الإطار مع مرور الوقت. أي عرض قرب نهاية الوقت يعيد تشغيل العد التنازلي.",
          tooltip: {
            observer: "وضع المراقب: المزايدة غير متاحة",
            waiting: "جارٍ انتظار بيانات الغرفة",
            leading: "أنت بالفعل في الصدارة",
            default: "قدّم العرض الأدنى التالي فوراً"
          }
        },
        packs: {
          title: "باقات المزايدة",
          summary: {
            uniform: "كل باقة تمنح {{count}} عروض",
            variable: "عدد العروض يختلف حسب الباقة",
            fallback: "كل باقة تمنح {{count}} عروض"
          },
          infiniteBids: "∞ عروض",
          bidsCount: "{{count}} عروض",
          remainingInfinite: "غير محدود",
          remaining: "{{count}} متبقية",
          tooltips: {
            purchase: "اضغط لشراء هذه الباقة",
            owned: "تمتلك هذه الباقة بالفعل",
            locked: "قم بفتح الباقة السابقة أولاً"
          },
          actions: {
            buyShort: "شراء",
            locked: "مغلقة",
            buyFull: "شراء الباقة",
            waitForPrevious: "انتظر الباقة رقم {{index}}"
          },
          status: {
            exhausted: "مستنفدة",
            current: "نشطة"
          },
          usageSummary: "{{used}}/{{total}} مستخدمة",
          cardLabel: "الباقة رقم {{index}}",
          footnote: "💡 تمنح الباقات 1 إلى 5 مجموعات عروض ثابتة. بعد الباقة 5، تدفع لكل عرض. تُعاد الأموال للعروض غير المستخدمة في الباقات عند الخسارة؛ بينما تتحول العروض المستخدمة إلى قيمة استرداد."
        },
        dialogs: {
          participation: {
            title: "تأكيد رسوم المشاركة",
            description: "الانضمام كمشارك يخصم رسوم المشاركة فوراً من محفظتك. تابع فقط إذا كنت موافقاً على الدفع الآن.",
            roomLabel: "غرفة المزاد",
            feeLabel: "رسوم المشاركة",
            walletBalance: "رصيد المحفظة",
            insufficientFunds: "لا تملك ما يكفي من الدينار لتغطية رسوم المشاركة. يرجى شحن المحفظة قبل الانضمام.",
            loading: "جارٍ جلب تفاصيل المزاد...",
            buttons: {
              viewAsObserver: "المشاهدة كمراقب",
              payAndJoin: "ادفع وانضم",
              join: "انضم كمشارك"
            }
          },
          packPurchase: {
            title: "شراء باقة المزايدة رقم {{index}}",
            description: "هذه الباقة مطلوبة لمتابعة المزايدة. كل باقة تفتح المستوى التالي. الباقة 5 تمنح مجموعة عروض ثابتة.",
            priceLabel: "السعر",
            autoPurchase: "شراء الباقة التالية تلقائياً عند الحاجة",
            buttons: {
              cancel: "إلغاء",
              processing: "جارٍ المعالجة...",
              confirm: "شراء الباقة"
            }
          }
        },
        common: {
          amountWithCurrency: "{{amount}} TND",
          currency: "TND"
        },
        winnerModal: {
          title: "مبروك!",
          subtitle: "لقد ربحت المزاد 🎉",
          winnerLabel: "الفائز",
          bidLabel: "العرض الفائز",
          actions: {
            viewDashboard: "عرض لوحة التحكم",
            keepBidding: "واصل المزايدة"
          }
        },
        notifications: {
          observerMode: "تم تفعيل وضع المراقب - استمتع بالبث المباشر!",
          joined: "تم الانضمام إلى غرفة المزاد بنجاح!",
          bidPlaced: "تم تسجيل العرض!",
          auctionExtended: "⏰ تم تمديد المزاد! وقت الانتهاء الجديد: {{time}}",
          auctionEnded: "🏁 انتهى المزاد!",
          youWon: "🎉 تهانينا! لقد ربحت المزاد! 🎉",
          noWinner: "انتهى المزاد دون فائز."
        }
      },
      
      // Dashboard
      installationSystemStatus: "حالة نظام التثبيت",
      monitorSystemDuringInstallation: "راقب نظامك أثناء التثبيت والإعداد",
      
      // Common
      welcome: "أهلاً وسهلاً",
      login: "تسجيل الدخول",
      register: "إنشاء حساب",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      confirmPassword: "تأكيد كلمة المرور",
      forgotPassword: "نسيت كلمة المرور؟",
      resetPassword: "إعادة تعيين كلمة المرور",
      submit: "إرسال",
      cancel: "إلغاء",
      save: "حفظ",
      delete: "حذف",
      edit: "تعديل",
      loading: "جاري التحميل...",
      error: "خطأ",
      success: "نجح",
      
      // Error pages
      pageNotFound: "الصفحة غير موجودة",
      pageNotFoundDescription: "الصفحة التي تبحث عنها غير موجودة.",
      goToLogin: "الذهاب لتسجيل الدخول",
      
      // History page
      history: {
        title: "السجل",
        subtitle: "عرض سجل المزايدات والمشتريات الخاصة بك"
      },

      // Received Offers page
      receivedOffers: {
        title: "العروض المستلمة",
        subtitle: "مراجعة والرد على العروض المقدمة على منتجاتك المستعملة"
      },

      // My Offers page
      myOffers: {
        title: "عروضي",
        subtitle: "تتبع حالة العروض التي قدمتها على المنتجات المستعملة"
      },

      // Unauthorized page
      unauthorized: {
        accessDenied: "تم رفض الوصول",
        noPermission: "ليس لديك صلاحية للوصول إلى هذه الصفحة.",
        currentAccessLevel: "مستوى الوصول الحالي",
        role: "الدور",
        roleNotAssigned: "غير محدد",
        permissions: "الصلاحيات",
        permissionsNone: "لا يوجد",
        goToHome: "الذهاب إلى الرئيسية",
        viewProfile: "عرض الملف الشخصي",
        contactAdmin: "إذا كنت تعتقد أن هذا خطأ، يرجى التواصل مع المسؤول."
      },

      // Auth page
      auth: {
        realTimeSecured: "مزادات فورية وآمنة",
        brandName: "Vitria",
        tagline: "زايد، بِع، وأدر الغرف بدون عناء.",
        loginDescription: "سجّل الدخول للوصول إلى الغرف المباشرة، أرصدة المحفظة، ولوحة عروضك.",
        settlement: "التسوية",
        settlementDesc: "دعم الضمان بالدينار التونسي",
        coverage: "التغطية",
        coverageDesc: "المزادات وغرف إعادة البيع",
        otpNotice: "يرجى التحقق من بريدك الإلكتروني للحصول على رمز التحقق لتفعيل حسابك.",
        emailVerified: "تم التحقق من البريد الإلكتروني! يمكنك الآن تسجيل الدخول.",
        resendCodeMessage: "يرجى التحقق من بريدك الإلكتروني للحصول على رمز جديد.",
        resendCodeNotice: "تم إرسال رمز تحقق جديد إلى بريدك الإلكتروني.",
        verifyEmailNotice: "يرجى التحقق من بريدك الإلكتروني. أدخل الرمز المرسل إلى بريدك أدناه."
      },

      // Notifications page
      notifications: {
        title: "الإشعارات",
        unread: "{{count}} غير مقروءة",
        markAllRead: "تحديد الكل كمقروء",
        deleteAll: "حذف الكل",
        deleteAllConfirm: "هل تريد حذف جميع الإشعارات؟",
        noNotifications: "لا توجد إشعارات.",
        markRead: "تحديد كمقروء",
        loadMore: "تحميل المزيد",
        newNotification: "إشعار جديد"
      },

      // Chat page
      chat: {
        contacts: "جهات الاتصال",
        noChatsYet: "لا توجد محادثات مفتوحة بعد.",
        product: "المنتج: {{name}}",
        offer: "العرض: {{amount}}",
        chatAbout: "محادثة حول: {{name}}",
        noMessages: "لا توجد رسائل بعد. قل مرحباً!",
        you: "أنت",
        partner: "الطرف الآخر",
        typePlaceholder: "اكتب رسالة",
        send: "إرسال",
        selectContact: "اختر جهة اتصال لبدء المحادثة.",
        generalChat: "محادثة عامة",
        unknownProduct: "منتج غير معروف"
      },

      // Profile page
      profile: {
        dashboard: "لوحة الملف الشخصي",
        subtitle: "إدارة معلومات حسابك وتفضيلاتك",
        memberSince: "عضو منذ {{year}}",
        history: "السجل",
        bidsAndPurchases: "المزايدات والمشتريات",
        myOffers: "عروضي",
        secondHandOffers: "عروض المستعمل",
        received: "المستلمة",
        offersOnListings: "العروض على القوائم",
        personalInfo: "المعلومات الشخصية",
        editProfile: "تعديل الملف الشخصي",
        firstName: "الاسم الأول",
        lastName: "اسم العائلة",
        username: "اسم المستخدم",
        emailAddress: "البريد الإلكتروني",
        role: "الدور",
        accountStatus: "حالة الحساب",
        active: "نشط"
      },

      // Product Details page
      productDetails: {
        backToMarketplace: "العودة إلى السوق",
        productNotFound: "المنتج غير موجود.",
        fetchError: "فشل في جلب تفاصيل المنتج.",
        soldOut: "نفذت الكمية",
        outOfStock: "غير متوفر",
        reviewCount: "({{count}} تقييمات)",
        quantity: "الكمية",
        unavailable: "غير متاح",
        buyNow: "اشترِ الآن",
        secureTransaction: "معاملة آمنة \u2022 بائع موثق \u2022 توصيل سريع",
        customerReviews: "تقييمات العملاء",
        noReviews: "لا توجد تقييمات بعد. كن أول من يقيّم!",
        userFallback: "مستخدم",
        writeReview: "اكتب تقييماً",
        rating: "التقييم",
        ratingExcellent: "5 - ممتاز",
        ratingGood: "4 - جيد",
        ratingAverage: "3 - متوسط",
        ratingPoor: "2 - ضعيف",
        ratingTerrible: "1 - سيء جداً",
        yourReview: "تقييمك",
        sharePlaceholder: "شارك تجربتك...",
        submitting: "جارٍ الإرسال...",
        submitReview: "إرسال التقييم",
        verifiedBuyersOnly: "يمكن للمشترين الموثقين فقط ترك تقييمات.",
        loginToReview: "سجّل الدخول واشترِ هذا المنتج لترك تقييم."
      },

      // Offer Details page
      offerDetails: {
        title: "تفاصيل العرض",
        failedToLoad: "فشل في تحميل تفاصيل العرض",
        payFeeConfirm: "هل تريد دفع {{amount}} TND لفتح المحادثة؟",
        feePaidSuccess: "تم دفع الرسوم بنجاح! المحادثة مفتوحة الآن.",
        failedToPayFee: "فشل في دفع الرسوم",
        offerActionSuccess: "تم {{action}} العرض بنجاح!",
        offerActionFailed: "فشل في {{action}} العرض",
        withdrawConfirm: "هل أنت متأكد من سحب هذا العرض؟",
        withdrawSuccess: "تم سحب العرض بنجاح!",
        failedToWithdraw: "فشل في سحب العرض",
        pleaseLogin: "يرجى تسجيل الدخول",
        loginRequired: "يجب تسجيل الدخول لعرض تفاصيل العرض.",
        backToMyOffers: "العودة إلى عروضي",
        backToReceivedOffers: "العودة إلى العروض المستلمة",
        offerNotFound: "العرض غير موجود",
        offerNotFoundDesc: "العرض الذي تبحث عنه غير موجود.",
        yourOfferOn: "عرضك على",
        offerReceivedFor: "عرض مستلم على",
        reservePrice: "السعر الاحتياطي",
        condition: "الحالة",
        category: "الفئة",
        yourOffer: "عرضك",
        status: "الحالة",
        timeLeft: "الوقت المتبقي",
        pendingActivation: "بانتظار التفعيل",
        expired: "منتهي الصلاحية",
        seller: "البائع",
        buyer: "المشتري",
        date: "التاريخ",
        message: "الرسالة",
        sellerResponseLabel: "رد البائع",
        actions: "الإجراءات",
        chatUnlocked: "المحادثة مفتوحة!",
        sellerChatMsg: "أكمل المشتري دفع الرسوم. ابدأ المحادثة لإتمام البيع.",
        buyerChatMsg: "يمكنك الآن التواصل مع البائع لإتمام الصفقة.",
        startChat: "💬 بدء المحادثة",
        sellerAwaitingFee: "⏳ بانتظار دفع المشتري لرسوم المحادثة.",
        buyerAwaitingFee: "⏳ قبل البائع عرضك. ادفع رسوم المحادثة لفتح المحادثة.",
        processing: "جارٍ المعالجة...",
        payFeeButton: "💰 دفع {{amount}} TND",
        optionalResponsePlaceholder: "رسالة رد اختيارية...",
        accept: "✅ قبول",
        reject: "❌ رفض",
        buyerNotifiedFee: "تم إخطار المشتري بدفع رسوم المحادثة. سيتم إعلامك بمجرد إكماله.",
        withdrawOffer: "🚫 سحب العرض",
        acceptOfferTitle: "قبول العرض وفتح المحادثة",
        acceptOfferDesc: "أكد القبول واختر المدة التي يملكها المشتري قبل انتهاء العرض.",
        productLabel: "المنتج",
        offerAmountLabel: "مبلغ العرض",
        chatFeeLabel: "رسوم المحادثة",
        foreverOption: "إلى الأبد (بدون انتهاء)",
        foreverDesc: "يبقى العرض نشطًا حتى تغلقه يدويًا.",
        countdownOption: "عد تنازلي",
        countdownDesc: "ينتهي بعد عدد الساعات المحدد (الحد الأقصى 720).",
        hours: "ساعات",
        provideDuration: "حدد مدة أو اختر إلى الأبد.",
        maxDuration: "الحد الأقصى المسموح هو 720 ساعة (30 يومًا).",
        confirmAcceptance: "تأكيد القبول"
      },

      // Rooms Bid page
      roomsBid: {
        title: "غرف المزادات",
        subtitle: "اختر المشاركة والمزايدة برموزك، أو راقب النشاط المباشر مجانًا.",
        free: "مجاني",
        noDeletePermission: "ليس لديك إذن لحذف الغرف.",
        deleteConfirm: "هل أنت متأكد من حذف غرفة المزاد هذه؟",
        deleteSuccess: "تم حذف غرفة المزاد بنجاح",
        deleteFailed: "فشل في حذف غرفة المزاد",
        deleteError: "خطأ في حذف غرفة المزاد",
        untitledRoom: "غرفة بدون عنوان",
        live: "مباشر",
        scheduled: "مجدولة",
        descriptionComingSoon: "الوصف قريبًا.",
        lastBid: "آخر مزايدة",
        timeRemaining: "الوقت المتبقي",
        participationFee: "رسوم المشاركة: {{fee}}",
        participate: "مشاركة",
        signInToParticipate: "سجّل الدخول للمشاركة",
        view: "مشاهدة",
        startingBid: "سعر البداية",
        startsIn: "يبدأ خلال",
        notYetAvailable: "غير متاح بعد",
        notStartedTooltip: "لم تبدأ غرفة المزاد هذه بعد",
        comingSoon: "قريبًا",
        createRoom: "إنشاء غرفة",
        activeAuctions: "المزادات النشطة",
        upcomingRooms: "الغرف القادمة",
        noRoomsTitle: "لا توجد غرف مزادات متاحة",
        noRoomsDesc: "لا توجد غرف مباشرة أو مجدولة حاليًا. تحقق لاحقًا أو أنشئ غرفة مزاد جديدة.",
        autoRefreshNotice: "ترقب\u2014يتم تحديث الغرف تلقائيًا كل 30 ثانية.",
        guestBanner: {
          title: "معاينة عامة للمزادات",
          description: "تصفح الغرف المباشرة والمجدولة بحرية. سجّل الدخول عندما تصبح جاهزًا للمشاركة أو المزايدة.",
          action: "تسجيل الدخول"
        }
      },

      // Auction Room Management page
      auctionManagement: {
        accessDenied: "تم رفض الوصول",
        noPermission: "ليس لديك صلاحية لإدارة غرف المزادات.",
        title: "إدارة غرف المزادات",
        createRoom: "إنشاء غرفة مزاد",
        unknownProduct: "منتج غير معروف",
        unknownUser: "مستخدم غير معروف",
        minBid: "الحد الأدنى للمزايدة:",
        bidIncrement: "زيادة المزايدة:",
        currentBid: "المزايدة الحالية:",
        noBid: "لا يوجد",
        participants: "المشاركون:",
        start: "البداية:",
        end: "النهاية:",
        mode: "الوضع:",
        history: {
          title: "سجل غرف المزادات",
          subtitle: "مراجعة الغرف المغلقة أو الملغاة وفحص تسلسل المزايدات.",
          closed: "مغلقة",
          cancelled: "ملغاة",
          recentRooms: "الغرف الأخيرة",
          noRooms: "لم يتم العثور على غرف لهذه الحالة بعد.",
          participants: "مشاركين",
          finalBid: "المزايدة النهائية",
          noCategory: "بدون فئة",
          finalPrice: "السعر النهائي",
          startTime: "وقت البدء",
          endTime: "وقت الانتهاء",
          duration: "المدة",
          winner: "الفائز",
          noWinner: "لا يوجد فائز",
          participationFee: "رسوم المشاركة",
          minBidIncrement: "الحد الأدنى / الزيادة",
          bidHistory: "سجل المزايدات",
          refreshing: "جارٍ التحديث\u2026",
          bidder: "المزايد",
          amount: "المبلغ",
          time: "الوقت",
          auto: "تلقائي",
          noBids: "لم يتم تسجيل أي مزايدات لهذه الغرفة.",
          selectRoom: "اختر غرفة لعرض سجلها."
        },
        form: {
          editTitle: "تعديل غرفة المزاد",
          createTitle: "إنشاء غرفة مزاد",
          product: "المنتج",
          selectProduct: "اختر منتجاً",
          reservableOnly: "يمكن استخدام المنتجات القابلة للحجز فقط لإنشاء غرف المزادات.",
          feeApplied: "رسوم المشاركة المطبقة: {{fee}}",
          feeFromProduct: "هذه القيمة مأخوذة مباشرة من إعدادات المنتج.",
          participationFee: "رسوم المشاركة (TND)",
          selectProductFirst: "اختر منتجاً أولاً",
          feeHelp: "يتم سحب هذه الرسوم مباشرة من المنتج المحدد. قم بتحديث المنتج لتغييرها.",
          minimumBid: "الحد الأدنى للمزايدة (TND)",
          bidIncrement: "زيادة المزايدة (TND)",
          startTime: "وقت البدء",
          durationMinutes: "المدة (دقائق)",
          computedEndTime: "وقت الانتهاء المحسوب",
          packIncrement: "زيادة الباقة (مزايدات لكل باقة)",
          derivedServerSide: "محسوب من الخادم",
          mode: "الوضع",
          fixed: "ثابت",
          autoExtend: "تمديد تلقائي",
          status: "الحالة",
          pending: "قيد الانتظار",
          active: "نشط",
          closed: "مغلق",
          cancelled: "ملغى",
          autoExtendDuration: "مدة التمديد التلقائي (ثوانٍ)",
          triggerThreshold: "عتبة التفعيل (الثواني المتبقية)",
          update: "تحديث غرفة المزاد",
          create: "إنشاء غرفة مزاد"
        },
        errors: {
          startTimeRequired: "وقت البدء والمدة مطلوبان",
          durationPositive: "يجب أن تكون المدة عدداً موجباً من الدقائق",
          endAfterStart: "يجب أن يكون وقت الانتهاء بعد وقت البدء",
          saveFailed: "خطأ في حفظ غرفة المزاد",
          deleteConfirm: "هل أنت متأكد من حذف غرفة المزاد هذه؟",
          deleteFailed: "خطأ في حذف غرفة المزاد"
        }
      },

      // Second Hand Rooms page
      roomsSecondHand: {
        title: "غرف المزادات المستعملة",
        subtitle: "اكتشف كنوزاً مستعملة في غرف مزادات منسقة. تتبع التوفر وأسعار الحد الأدنى وتواصل مع البائعين مباشرة بعروض آمنة.",
        requestRoom: "طلب غرفة مزاد",
        status: {
          active: "مباشر",
          pending: "بانتظار الموافقة",
          expired: "منتهي",
          sold: "مباع",
          closed: "مغلق",
          canceled: "مغلق",
          scheduled: "مجدول"
        },
        condition: {
          like_new: "شبه جديد",
          excellent: "ممتاز",
          good: "جيد",
          used: "مستعمل",
          fair: "مقبول",
          poor: "ضعيف"
        },
        duration: {
          dayLeft: "يوم واحد متبقي",
          daysLeft: "{{count}} أيام متبقية",
          daysHoursLeft: "{{days}} يوم {{hours}} ساعة متبقية",
          hoursMinutesLeft: "{{hours}} ساعة {{minutes}} دقيقة متبقية"
        },
        card: {
          unnamed: "منتج بدون اسم",
          general: "عام",
          noDescription: "لا يوجد وصف.",
          reservePrice: "السعر المطلوب",
          openToOffers: "مفتوح للعروض",
          duration: "المدة",
          condition: "الحالة",
          seller: "البائع",
          anonymous: "مجهول",
          makeOffer: "قدم عرضاً"
        },
        empty: {
          title: "لا توجد عناصر مطابقة",
          subtitle: "تحقق لاحقاً؛ تصل طلبات جديدة كل بضع دقائق بمجرد أن يقدم البائعون غرفهم."
        },
        sellerForm: {
          title: "طلب غرفة مزاد",
          reviewNotice: "يتم مراجعة طلبك من قبل المسؤول. بمجرد الموافقة، ستبدأ غرفة المزاد باستخدام قواعد المدة التي تحددها أدناه.",
          tndNotice: "ضمانات TND تحمي الطرفين. راجع القواعد قبل تقديم إعلانك.",
          viewRules: "عرض قواعد ضمان TND",
          productName: "اسم المنتج",
          category: "الفئة",
          selectCategory: "اختر فئة",
          description: "الوصف",
          reservePrice: "السعر المطلوب (TND)",
          reservePriceHelp: "اختياري — اتركه فارغاً لاستقبال أي عرض",
          condition: "الحالة",
          uploadImages: "رفع الصور",
          uploadHelp: "ارفع حتى 5 صور (بحد أقصى 10 ميغابايت لكل صورة).",
          filesSelected: "{{count}} ملف(ات) محددة",
          listingVisibility: "ظهور الإعلان",
          listingVisibilityHelp: "اختر مدة صلاحية العروض المقبولة افتراضياً. اختيار \"لانهائي\" يخفي المدة من الإعلانات.",
          infinite: "لانهائي (\u221e)",
          countdown: "عد تنازلي",
          defaultDuration: "المدة الافتراضية (ساعات)",
          maxDuration: "الحد الأقصى 720 ساعة (30 يوماً).",
          submitting: "جارٍ الإرسال\u2026",
          submitRequest: "إرسال الطلب"
        },
        offerModal: {
          title: "قدم عرضاً",
          reservePrice: "السعر الاحتياطي: {{price}}",
          lockNotice: "تقديم عرض يحجز مؤقتاً 1 TND حتى يرد البائع.",
          viewRules: "عرض قواعد ضمان TND",
          offerLabel: "عرضك (TND)",
          submitting: "جارٍ الإرسال\u2026",
          submitOffer: "إرسال العرض"
        },
        toasts: {
          loginRequired: "يرجى تسجيل الدخول لتقديم عرض.",
          ownProduct: "لا يمكنك تقديم عرض على منتجك الخاص.",
          invalidAmount: "يرجى إدخال مبلغ عرض صالح.",
          offerSuccess: "تم إرسال العرض بنجاح! سيراجع البائع عرضك.",
          offerFailed: "فشل في إرسال العرض.",
          offerFailedRetry: "فشل في إرسال العرض. يرجى المحاولة مرة أخرى.",
          fillRequired: "يرجى ملء جميع الحقول المطلوبة.",
          pricePositive: "يجب أن يكون سعر البداية أكبر من 0.",
          reserveMinimum: "يجب أن يكون السعر الاحتياطي أكبر من أو يساوي سعر البداية.",
          requestSuccess: "تم إرسال طلب غرفة المزاد! سيراجعه المسؤول.",
          requestFailed: "فشل في إرسال الطلب.",
          requestFailedRetry: "فشل في إرسال الطلب. يرجى المحاولة مرة أخرى."
        }
      },

      // Payment page
      payment: {
        noProductId: "لم يتم توفير معرّف المنتج",
        productNotFoundTitle: "المنتج غير موجود",
        invalidResponse: "تنسيق الاستجابة غير صالح",
        nameRequired: "الاسم مطلوب",
        emailRequired: "البريد الإلكتروني مطلوب",
        cardRequired: "رقم البطاقة مطلوب",
        expiryRequired: "تاريخ الانتهاء مطلوب",
        cvcRequired: "رمز CVC مطلوب",
        addressRequired: "العنوان مطلوب",
        cityRequired: "المدينة مطلوبة",
        postalCodeRequired: "الرمز البريدي مطلوب",
        invalidCardLength: "طول رقم البطاقة غير صالح",
        invalidExpiryFormat: "تنسيق الانتهاء غير صالح (MM/YY)",
        invalidCvcLength: "طول رمز CVC غير صالح",
        loginRequired: "يرجى تسجيل الدخول لإتمام الشراء.",
        fixErrors: "يرجى تصحيح الأخطاء التالية:",
        purchaseFailed: "فشل الشراء",
        paymentSuccessRedirect: "تم الدفع بنجاح! تمت معالجة طلب {{name}}.",
        unexpectedResponse: "استجابة غير متوقعة من الخادم",
        paymentFailed: "فشل الدفع. يرجى المحاولة مرة أخرى.",
        loadingProduct: "جارٍ تحميل تفاصيل المنتج...",
        backToProducts: "العودة إلى المنتجات",
        paymentSuccess: "تم الدفع بنجاح!",
        orderProcessed: "تمت معالجة طلبك لـ {{name}}.",
        totalPaid: "الإجمالي المدفوع",
        quantity: "الكمية",
        redirecting: "جارٍ التوجيه إلى صفحة المنتجات...",
        orderSummary: "ملخص الطلب",
        soldOut: "نفدت الكمية",
        subtotal: "المجموع الفرعي",
        shipping: "الشحن",
        free: "مجاني",
        tax: "الضريبة (20%)",
        total: "الإجمالي",
        paymentInfo: "معلومات الدفع",
        creditCardDetails: "تفاصيل بطاقة الائتمان",
        fullName: "الاسم الكامل",
        cardNumber: "رقم البطاقة",
        expiryDate: "تاريخ الانتهاء",
        cvc: "CVC",
        billingAddress: "عنوان الفواتير",
        address: "العنوان",
        city: "المدينة",
        postalCode: "الرمز البريدي",
        payAmount: "دفع {{amount}}",
        purchaseWithWallet: "شراء عبر {{wallet}}",
        recoveryWallet: "محفظة الاسترداد",
        mainWallet: "المحفظة الرئيسية",
        completePurchaseAuto: "إتمام الشراء (محسّن تلقائيًا)"
      },

      // Theme
      darkMode: "الوضع المظلم",
      lightMode: "الوضع المضيء",
      
      // Language
      language: "اللغة",
      english: "English",
      arabic: "العربية",
      french: "Français"
    }
  },
  fr: {
    translation: {
      // Navigation
      nav: {
        home: "Accueil",
        rooms: "Salles",
        bidRooms: "Salles d'enchères",
        secondHand: "Occasion",
        ecommerce: "E-commerce",
        profile: "Profil",
        history: "Historique",
        settings: "Paramètres",
        dashboard: "Tableau de bord",
        logout: "Se déconnecter",
        login: "Se connecter",
        management: "Gestion",
        adminDashboard: "Tableau d'administration",
        recoveryWallet: "Portefeuille de récupération",
        wallet: "Portefeuille",
        notifications: "Notifications",
        more: "Plus",
        myOffers: "Mes offres",
        receivedOffers: "Offres reçues",
        systemDashboard: "Tableau système",
        analytics: "Analyses",
        preloaderTest: "Test de préchargement"
      },
      
      // Hero Section
      hero: {
        slide1: {
          title: "Bienvenue dans le futur",
          subtitle: "Découvrez la technologie de nouvelle génération",
          description: "Découvrez des solutions innovantes qui transforment votre façon de travailler et de vivre.",
          cta: "Explorer maintenant"
        },
        slide2: {
          title: "Analyses avancées",
          subtitle: "Des insights basés sur les données à portée de main",
          description: "Exploitez la puissance de l'IA et de l'apprentissage automatique pour des décisions plus intelligentes.",
          cta: "En savoir plus"
        },
        slide3: {
          title: "Intégration transparente",
          subtitle: "Connectez tout, partout",
          description: "Créez des flux de travail puissants qui intègrent tous vos outils préférés.",
          cta: "Commencer"
        },
        watchDemo: "Regarder la démo"
      },
      
      // Home Page
      home: {
        welcome: "Bon retour, {{name}} !",
        welcomeLabel: "Bon retour",
        welcomeGreeting: "Bonjour de nouveau,",
        userFallback: "Utilisateur",
        welcomeDesc: "Votre marché numérique vous attend. Explorez les possibilités infinies.",
        stats: {
          users: "Utilisateurs",
          products: "Produits",
          auctions: "Enchères",
          transactions: "Transactions"
        },
        quickActions: {
          title: "Actions rapides",
          subtitle: "Accédez directement à ce dont vous avez besoin",
          status: "Tous les systèmes sont opérationnels",
          ecommerce: "E-commerce",
          ecommerceDesc: "Parcourez les produits et magasinez",
          auctions: "Enchères",
          auctionsDesc: "Participez aux enchères en direct",
          secondHand: "Occasion",
          secondHandDesc: "Achetez et vendez des articles d'occasion",
          dashboard: "Tableau de bord",
          dashboardDesc: "Gérez votre compte",
          ctaHint: "Commencer"
        },
        features: {
          title: "Pourquoi nous choisir",
          subtitle: "Découvrez l'avenir du commerce numérique",
          realTime: "Mises à jour en temps réel",
          realTimeDesc: "Recevez des notifications instantanées et des mises à jour en direct",
          secure: "Transactions sécurisées",
          secureDesc: "Sécurité de niveau bancaire pour toutes les transactions",
          mobile: "Optimisé pour mobile",
          mobileDesc: "Expérience parfaite sur n'importe quel appareil",
          support: "Support 24/7",
          supportDesc: "Support client 24 heures sur 24"
        },
        featuredRooms: {
          title: "Chambres d'enchères en vedette",
          subtitle: "Découvrez des enchères premium avec des articles exclusifs",
          viewAll: "Voir toutes les chambres d'enchères"
        },
        lastMinute: {
          title: "Offres de dernière minute",
          subtitle: "Enchères se terminant bientôt - ne manquez pas !"
        },
        recentProducts: {
          title: "Produits récents",
          subtitle: "Nouveaux arrivages dans notre marché",
          viewAll: "Voir tous les produits"
        },
        secondHand: {
          title: "Trésors d'occasion",
          subtitle: "Articles de seconde main de qualité à des prix avantageux",
          viewAll: "Voir tous les articles d'occasion"
        },
        secretBids: {
          title: "Enchères secrètes",
          subtitle: "Enchères cachées pour les aventuriers"
        },
        cta: {
          title: "Prêt à commencer votre voyage ?",
          description: "Rejoignez des milliers d'utilisateurs qui découvrent déjà l'avenir du commerce numérique.",
          getStarted: "Commencer",
          goToDashboard: "Aller au tableau de bord"
        },
        errors: {
          userData: "Échec du chargement des données utilisateur. Veuillez réessayer.",
          stats: "Échec du chargement des statistiques de la plateforme.",
          featured: "Échec du chargement des salles d'enchères en vedette.",
          lastMinute: "Échec du chargement des offres de dernière minute.",
          recentProducts: "Échec du chargement des produits récents.",
          secondHand: "Échec du chargement des produits d'occasion.",
          secretBids: "Échec du chargement des enchères secrètes."
        },
        hero: {
          badge: "Le marché n°1 de Tunisie pour les enchères",
          title1: "Gagnez gros.",
          title2: "Achetez malin.",
          title3: "Vendez facilement.",
          subtitle: "Rejoignez des enchères en direct, découvrez de bonnes affaires dans notre boutique, ou mettez en vente vos articles sur la place de marché la plus fiable de Tunisie.",
          browseAuctions: "Parcourir les enchères",
          shopNow: "Acheter maintenant",
          sellItem: "Vendre un article",
          comingSoon: "Enchères à venir bientôt",
          comingSoonDesc: "De nouvelles salles ouvrent chaque jour. Soyez le premier à enchérir.",
          exploreRooms: "Explorer les salles"
        },
        members: "Membres",
        auctionsRun: "Enchères organisées",
        productsListed: "Produits listés",
        featuredOverline: "Salles en vedette",
        featuredEmpty: "Aucune enchère planifiée pour l'instant — revenez bientôt !",
        browseAllRooms: "Voir toutes les salles",
        endingSoon: {
          overline: "Sensible au temps",
          title: "Se termine bientôt",
          viewAll: "Toutes les salles actives",
          currentBid: "Enchère actuelle",
          startsBid: "commence à"
        },
        recentOverline: "Boutique en ligne",
        newArrivals: "Nouveautés",
        noProducts: "Aucun produit listé pour l'instant.",
        goToShop: "Aller à la boutique",
        howItWorks: {
          overline: "Commencez en quelques minutes",
          title: "Comment ça marche",
          step1Title: "Créez votre compte",
          step1Desc: "Inscrivez-vous gratuitement en moins d'une minute. Vérifiez votre e-mail et vous êtes prêt.",
          step2Title: "Parcourez et enchérissez",
          step2Desc: "Découvrez des salles d'enchères en direct, des produits ou des trésors d'occasion.",
          step3Title: "Gagnez et recevez votre commande",
          step3Desc: "Paiement sécurisé via notre système d'entiercement. L'article est expédié une fois la transaction confirmée.",
          trustEscrow: "Paiements sécurisés par entiercement",
          trustRealtime: "Enchères en temps réel",
          trustCommunity: "Communauté active",
          trustWinners: "Gagnants vérifiés"
        },
        ctaHeadline: "Prêt à commencer les enchères ?",
        ctaSubtext: "Rejoignez des milliers d'acheteurs et vendeurs tunisiens déjà sur Vitria. L'inscription est gratuite et prend 60 secondes.",
        createAccount: "Créer un compte gratuit",
        browseWithout: "Parcourir sans compte",
        footer: {
          copyright: "© {{year}} Vitria. Tous droits réservés.",
          tagline: "La première plateforme d'enchères et e-commerce de Tunisie. Enchérissez en direct, achetez malin, vendez en toute confiance.",
          marketplace: "Marketplace",
          liveAuctions: "Enchères en direct",
          onlineShop: "Boutique en ligne",
          secondHand: "Occasion",
          myDashboard: "Mon tableau de bord",
          account: "Compte",
          signUp: "S'inscrire",
          logIn: "Se connecter",
          profile: "Profil",
          settings: "Paramètres",
          support: "Support",
          helpCenter: "Centre d'aide",
          contactUs: "Contactez-nous",
          terms: "Conditions d'utilisation",
          privacy: "Politique de confidentialité",
          contactTitle: "Contact",
          madeWith: "Fait avec ❤️ en Tunisie"
        }
      },
      
      ecommerce: {
        title: "Marketplace",
        subtitle: "Découvrez des lancements sélectionnés et un inventaire prêt pour les enchères dans une expérience riche en données.",
        search: {
          placeholder: "Que recherchez-vous ?"
        },
        filter: {
          title: "Parcourir par catégorie",
          subtitle: "Touchez pour filtrer le catalogue"
        },
        categories: {
          defaultHelper: "Filtrer par catégorie",
          all: {
            label: "Toutes les catégories",
            helper: "Afficher tout"
          },
          realestate: {
            label: "Immobilier",
            helper: "Filtrer par catégorie"
          },
          property: {
            label: "Propriété",
            helper: "Filtrer par catégorie"
          },
          vehicles: {
            label: "Véhicules",
            helper: "Filtrer par catégorie"
          },
          electronicstech: {
            label: "Électronique & Tech",
            helper: "Filtrer par catégorie"
          },
          homegarden: {
            label: "Maison & Jardin",
            helper: "Filtrer par catégorie"
          },
          entertainment: {
            label: "Divertissement",
            helper: "Filtrer par catégorie"
          },
          fashionbeauty: {
            label: "Mode & Beauté",
            helper: "Filtrer par catégorie"
          },
          jobsservices: {
            label: "Emplois & Services",
            helper: "Filtrer par catégorie"
          },
          business: {
            label: "Affaires",
            helper: "Filtrer par catégorie"
          },
          other: {
            label: "Autres",
            helper: "Filtrer par catégorie"
          }
        },
        product: {
          soldOut: "Épuisé",
          auction: "Enchère",
          unnamed: "Produit sans nom",
          genericCategory: "Général",
          imageAlt: "Image du produit",
          noDescription: "Aucune description disponible",
          priceLabel: "Prix",
          reservationStatus: "Statut de réservation",
          percentFull: "{{percent}} % complet",
          statusFull: "Toutes les places sont confirmées",
          statusFast: "Ça se remplit vite - réservez maintenant",
          statusAvailable: "Places disponibles",
          participationFeeLabel: "Frais de participation",
          participationFeeTokens: "{{count}} TND",
          unavailable: "Indisponible",
          buyNow: "Acheter maintenant",
          reserve: "Réserver une place",
          reserved: "Réservé",
          reserving: "Réservation...",
          reserveTooltip: {
            full: "Toutes les places de réservation sont prises",
            owned: "Vous avez déjà réservé une place",
            default: "Réservez votre place aux enchères"
          },
          startingBid: "Mise de départ : {{amount}}"
        },
        empty: {
          title: "Aucun produit trouvé",
          subtitle: "Essayez d'ajuster les filtres ou revenez bientôt pour de nouvelles sélections."
        },
        dialog: {
          title: "Réserver une place aux enchères",
          infoMessage: "Sécurisez votre place avant que la salle ne soit complète. Le paiement n'est requis que lorsque la capacité est atteinte.",
          totalReservations: "Réservations totales",
          spotsRemaining: "{{count}} places restantes sur {{cap}}",
          confirm: "Confirmer la réservation"
        },
        reservationResult: {
          full: "Enchère confirmée ! Vous avez obtenu la dernière place.",
          success: "Réservation réussie. Nous vous informerons à mesure que l'enchère se remplit.",
          errorDefault: "Échec de la création de la réservation.",
          errorCatch: "Impossible de créer la réservation. Veuillez réessayer."
        },
        errors: {
          invalidResponse: "Structure de réponse de l'API invalide",
          loadProducts: "Impossible de charger les produits. Veuillez réessayer plus tard."
        }
      },

      // Common
      common: {
        loading: "Chargement...",
        bidders: "Enchérisseurs",
        currentBid: "Offre actuelle",
        endsIn: "Se termine dans",
        placeBid: "Faire une offre",
        urgent: "URGENT",
        bidNow: "Enchérir maintenant",
        buyNow: "Acheter maintenant",
        viewDetails: "Voir les détails",
        secret: "SECRET",
        startingPrice: "Prix de départ",
        participants: "Participants",
        joinSecret: "Rejoindre le secret",
        learnMore: "En savoir plus",
        retry: "Réessayer",
        noData: "Aucune donnée trouvée",
        close: "Fermer"
      },

      navbar: {
        manageProducts: "Produits",
        manageAuctionRooms: "Salles d'enchères",
        markAllRead: "Tout marquer comme lu",
        noNotifications: "Aucune notification pour le moment",
        yourWallet: "Votre portefeuille",
  mainTokens: "TND principal",
        quickSettings: "Paramètres rapides",
        menu: "Menu",
  tokensChip: "{{count}} TND",
        recoveryChip: "{{count}} récupération",
        quickLinks: "Liens rapides",
        claimCoupon: "Utiliser un coupon",
        guest: "Invité"
      },
      liveAuction: {
        loading: {
          title: "Connexion à la salle d'enchères...",
          description: "Merci de patienter pendant la synchronisation des données d'enchères en direct."
        },
        connection: {
          refreshTooltip: "Actualiser la connexion",
          labels: {
            connected: "Connecté",
            connecting: "Connexion...",
            disconnected: "Déconnecté",
            guest: "Aperçu en lecture seule"
          }
        },
        alerts: {
          reconnecting: "Reconnexion en cours..."
        },
        errors: {
          outbidWithAmount: "Vous avez été surenchéri ! Nouvelle enchère la plus élevée : {{amount}}",
          outbid: "Vous avez été surenchéri ! Déposez une enchère plus élevée pour reprendre la tête.",
          observerCannotBid: "Mode observateur : les enchères sont désactivées.",
          cannotCalculateNextBid: "Impossible de calculer la prochaine enchère.",
          alreadyLeading: "Vous êtes déjà le meilleur enchérisseur.",
          observerCannotBuyPacks: "L'achat de packs est réservé aux participants.",
          insufficientFundsRedirect: "TND insuffisants. Redirection vers la page de paiement...",
          packPurchaseFailed: "Impossible d'acheter le pack.",
          authRequired: "Authentification requise.",
          connectionLost: "Connexion perdue. Tentative de reconnexion...",
          connectionFailed: "Impossible de se connecter à la salle d'enchères.",
          authFailed: "Échec de l'authentification : {{message}}",
          roomLoadFailed: "Impossible de charger cette salle d'enchères. Veuillez réessayer."
        },
        success: {
          packPurchased: "Pack n°{{index}} acheté"
        },
        fallback: {
          productCategory: "Produit en enchère",
          product: "Produit en enchère",
          room: "Salle d'enchères"
        },
        mode: {
          observer: "Mode observateur",
          participant: "Mode participant",
          guest: "Aperçu visiteur"
        },
        observerNotice: "Vous observez cette salle en direct. Utilisez le bouton \"Participer\" depuis la liste des salles pour rejoindre l'enchère.",
        guest: {
          title: "Vous regardez en lecture seule",
          description: "Les informations et les prix restent actualisés. Connectez-vous pour payer les frais, acheter des packs ou enchérir.",
          signIn: "Se connecter",
          signInToBid: "Se connecter pour enchérir",
          signInTooltip: "Connectez-vous pour participer à cette enchère",
          bidCaption: "Les visiteurs peuvent suivre l'enchère, mais seuls les participants connectés peuvent miser.",
          currentPriceCaption: "Prix public en direct — connectez-vous pour participer.",
          refreshTooltip: "Actualiser les données de la salle",
          refreshed: "Données de la salle actualisées"
        },
        countdown: {
          label: "Temps restant",
          ended: "Enchère terminée",
          startsIn: "Commence dans",
          starting: "Démarrage imminent",
          daysHours: "{{days}}j {{hours}}h",
          hoursMinutes: "{{hours}}h {{minutes}}min",
          minutesSeconds: "{{minutes}}min {{seconds}}s"
        },
        highestBid: {
          label: "Enchère la plus élevée",
          leader: "Vous êtes en tête !",
          trailing: "Restez attentif et enchérez avec stratégie."
        },
        quickBid: {
          buttonWithAmount: "Prochaine enchère • {{amount}}",
          button: "Prochaine enchère",
          caption: "La bordure se réduit avec le temps. Toute enchère déposée près du buzzer relance le compte à rebours.",
          tooltip: {
            observer: "Mode observateur : les mises sont désactivées",
            waiting: "En attente des données de la salle",
            leading: "Vous êtes déjà en tête",
            default: "Déposez instantanément la prochaine enchère minimale"
          }
        },
        packs: {
          title: "Packs d'enchères",
          summary: {
            uniform: "Chaque pack offre {{count}} enchères",
            variable: "Nombre d'enchères variable selon le pack",
            fallback: "Chaque pack offre {{count}} enchères"
          },
          infiniteBids: "∞ enchères",
          bidsCount: "{{count}} enchères",
          remainingInfinite: "Illimité",
          remaining: "{{count}} restantes",
          tooltips: {
            purchase: "Touchez pour acheter ce pack",
            owned: "Pack déjà possédé",
            locked: "Débloquez d'abord le pack précédent"
          },
          actions: {
            buyShort: "Acheter",
            locked: "Verrouillé",
            buyFull: "Acheter le pack",
            waitForPrevious: "Attendez le pack n°{{index}}"
          },
          status: {
            exhausted: "Épuisé",
            current: "Actuel"
          },
          usageSummary: "{{used}}/{{total}} utilisées",
          cardLabel: "Pack n°{{index}}",
          footnote: "💡 Les packs 1 à 5 offrent des lots d'enchères fixes. Après le pack n°5, vous payez par enchère. Les enchères inutilisées des packs sont remboursées en cas de défaite ; les enchères utilisées se convertissent en valeur de récupération."
        },
        dialogs: {
          participation: {
            title: "Confirmer les frais de participation",
            description: "Rejoindre en tant que participant déduit immédiatement les frais de participation de votre portefeuille. Continuez uniquement si vous acceptez de régler ce montant maintenant.",
            roomLabel: "Salle d'enchères",
            feeLabel: "Frais de participation",
            walletBalance: "Solde du portefeuille",
            insufficientFunds: "Vous n'avez pas assez de TND pour couvrir les frais de participation. Merci d'alimenter votre portefeuille avant de rejoindre.",
            loading: "Récupération des détails de l'enchère...",
            buttons: {
              viewAsObserver: "Voir en observateur",
              payAndJoin: "Payer et rejoindre",
              join: "Rejoindre comme participant"
            }
          },
          packPurchase: {
            title: "Acheter le pack d'enchères n°{{index}}",
            description: "Ce pack est requis pour continuer à enchérir. Chaque pack ouvre l'accès au palier suivant. Le pack n°5 offre un lot d'enchères fixe.",
            priceLabel: "Prix",
            autoPurchase: "Acheter automatiquement le prochain pack si nécessaire",
            buttons: {
              cancel: "Annuler",
              processing: "Traitement...",
              confirm: "Acheter le pack"
            }
          }
        },
        common: {
          amountWithCurrency: "{{amount}} TND",
          currency: "TND"
        },
        winnerModal: {
          title: "Félicitations !",
          subtitle: "Vous avez remporté l'enchère 🎉",
          winnerLabel: "Gagnant",
          bidLabel: "Enchère gagnante",
          actions: {
            viewDashboard: "Voir le tableau de bord",
            keepBidding: "Continuer à enchérir"
          }
        },
        notifications: {
          observerMode: "Mode observateur activé - profitez du direct !",
          joined: "Salle d'enchères rejointe avec succès !",
          bidPlaced: "Enchère enregistrée !",
          auctionExtended: "⏰ Enchère prolongée ! Nouvelle heure de fin : {{time}}",
          auctionEnded: "🏁 L'enchère est terminée !",
          youWon: "🎉 Bravo ! Vous avez remporté l'enchère ! 🎉",
          noWinner: "L'enchère s'est terminée sans gagnant."
        }
      },
      
      // Dashboard
      installationSystemStatus: "Statut du système d'installation",
      monitorSystemDuringInstallation: "Surveillez votre système pendant l'installation et la configuration",
      
      // Common
      welcome: "Bienvenue",
      login: "Se connecter",
      register: "S'inscrire",
      email: "Email",
      password: "Mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      forgotPassword: "Mot de passe oublié ?",
      resetPassword: "Réinitialiser le mot de passe",
      submit: "Soumettre",
      cancel: "Annuler",
      save: "Sauvegarder",
      delete: "Supprimer",
      edit: "Modifier",
      loading: "Chargement...",
      error: "Erreur",
      success: "Succès",
      
      // Error pages
      pageNotFound: "Page non trouvée",
      pageNotFoundDescription: "La page que vous recherchez n'existe pas.",
      goToLogin: "Aller à la connexion",
      
      // History page
      history: {
        title: "Historique",
        subtitle: "Consultez votre historique d'enchères et d'achats"
      },

      // Received Offers page
      receivedOffers: {
        title: "Offres reçues",
        subtitle: "Examinez et répondez aux offres sur vos produits d'occasion"
      },

      // My Offers page
      myOffers: {
        title: "Mes offres",
        subtitle: "Suivez le statut des offres que vous avez faites sur des produits d'occasion"
      },

      // Unauthorized page
      unauthorized: {
        accessDenied: "Accès refusé",
        noPermission: "Vous n'avez pas la permission d'accéder à cette page.",
        currentAccessLevel: "Votre niveau d'accès actuel",
        role: "Rôle",
        roleNotAssigned: "Non attribué",
        permissions: "Permissions",
        permissionsNone: "Aucune",
        goToHome: "Aller à l'accueil",
        viewProfile: "Voir le profil",
        contactAdmin: "Si vous pensez qu'il s'agit d'une erreur, veuillez contacter un administrateur."
      },

      // Auth page
      auth: {
        realTimeSecured: "Enchères en temps réel, sécurisées",
        brandName: "Vitria",
        tagline: "Enchérissez, vendez et gérez vos salles sans friction.",
        loginDescription: "Connectez-vous pour accéder aux salles en direct, aux soldes de portefeuille et à votre tableau de bord d'offres.",
        settlement: "Règlement",
        settlementDesc: "Support d'entiercement TND",
        coverage: "Couverture",
        coverageDesc: "Enchères et salles de revente",
        otpNotice: "Veuillez vérifier votre e-mail pour un code de vérification afin d'activer votre compte.",
        emailVerified: "E-mail vérifié ! Vous pouvez maintenant vous connecter.",
        resendCodeMessage: "Veuillez vérifier votre e-mail pour un nouveau code.",
        resendCodeNotice: "Un nouveau code de vérification a été envoyé à votre e-mail.",
        verifyEmailNotice: "Veuillez vérifier votre e-mail. Entrez le code envoyé à votre e-mail ci-dessous."
      },

      // Notifications page
      notifications: {
        title: "Notifications",
        unread: "{{count}} non lues",
        markAllRead: "Tout marquer comme lu",
        deleteAll: "Tout supprimer",
        deleteAllConfirm: "Supprimer toutes les notifications ?",
        noNotifications: "Aucune notification.",
        markRead: "Marquer comme lu",
        loadMore: "Charger plus",
        newNotification: "Nouvelle notification"
      },

      // Chat page
      chat: {
        contacts: "Contacts",
        noChatsYet: "Aucune conversation déverrouillée pour le moment.",
        product: "Produit : {{name}}",
        offer: "Offre : {{amount}}",
        chatAbout: "Discussion sur : {{name}}",
        noMessages: "Aucun message pour le moment. Dites bonjour !",
        you: "Vous",
        partner: "Partenaire",
        typePlaceholder: "Tapez un message",
        send: "Envoyer",
        selectContact: "Sélectionnez un contact pour commencer à discuter.",
        generalChat: "Discussion générale",
        unknownProduct: "Produit inconnu"
      },

      // Profile page
      profile: {
        dashboard: "Tableau de bord du profil",
        subtitle: "Gérez les informations et les préférences de votre compte",
        memberSince: "Membre depuis {{year}}",
        history: "Historique",
        bidsAndPurchases: "Enchères et achats",
        myOffers: "Mes offres",
        secondHandOffers: "Offres d'occasion",
        received: "Reçues",
        offersOnListings: "Offres sur les annonces",
        personalInfo: "Informations personnelles",
        editProfile: "Modifier le profil",
        firstName: "Prénom",
        lastName: "Nom de famille",
        username: "Nom d'utilisateur",
        emailAddress: "Adresse e-mail",
        role: "Rôle",
        accountStatus: "Statut du compte",
        active: "Actif"
      },

      // Product Details page
      productDetails: {
        backToMarketplace: "Retour au marché",
        productNotFound: "Produit introuvable.",
        fetchError: "Échec du chargement des détails du produit.",
        soldOut: "ÉPUISÉ",
        outOfStock: "Rupture de stock",
        reviewCount: "({{count}} avis)",
        quantity: "Quantité",
        unavailable: "Indisponible",
        buyNow: "Acheter maintenant",
        secureTransaction: "Transaction sécurisée \u2022 Vendeur vérifié \u2022 Livraison rapide",
        customerReviews: "Avis des clients",
        noReviews: "Aucun avis pour le moment. Soyez le premier à donner votre avis !",
        userFallback: "Utilisateur",
        writeReview: "Écrire un avis",
        rating: "Note",
        ratingExcellent: "5 - Excellent",
        ratingGood: "4 - Bon",
        ratingAverage: "3 - Moyen",
        ratingPoor: "2 - Médiocre",
        ratingTerrible: "1 - Terrible",
        yourReview: "Votre avis",
        sharePlaceholder: "Partagez votre expérience...",
        submitting: "Envoi en cours...",
        submitReview: "Soumettre l'avis",
        verifiedBuyersOnly: "Seuls les acheteurs vérifiés peuvent laisser des avis.",
        loginToReview: "Connectez-vous et achetez ce produit pour laisser un avis."
      },

      // Offer Details page
      offerDetails: {
        title: "Détails de l'offre",
        failedToLoad: "Échec du chargement des détails de l'offre",
        payFeeConfirm: "Payer {{amount}} TND pour déverrouiller le chat ?",
        feePaidSuccess: "Frais payés avec succès ! Le chat est maintenant déverrouillé.",
        failedToPayFee: "Échec du paiement des frais",
        offerActionSuccess: "Offre {{action}}ée avec succès !",
        offerActionFailed: "Échec de l'action {{action}} sur l'offre",
        withdrawConfirm: "Êtes-vous sûr de vouloir retirer cette offre ?",
        withdrawSuccess: "Offre retirée avec succès !",
        failedToWithdraw: "Échec du retrait de l'offre",
        pleaseLogin: "Veuillez vous connecter",
        loginRequired: "Vous devez être connecté pour voir les détails de l'offre.",
        backToMyOffers: "Retour à mes offres",
        backToReceivedOffers: "Retour aux offres reçues",
        offerNotFound: "Offre introuvable",
        offerNotFoundDesc: "L'offre que vous recherchez n'existe pas.",
        yourOfferOn: "Votre offre sur",
        offerReceivedFor: "Offre reçue pour",
        reservePrice: "Prix de réserve",
        condition: "État",
        category: "Catégorie",
        yourOffer: "Votre offre",
        status: "Statut",
        timeLeft: "Temps restant",
        pendingActivation: "En attente d'activation",
        expired: "Expiré",
        seller: "Vendeur",
        buyer: "Acheteur",
        date: "Date",
        message: "Message",
        sellerResponseLabel: "Réponse du vendeur",
        actions: "Actions",
        chatUnlocked: "Chat déverrouillé !",
        sellerChatMsg: "L'acheteur a payé les frais. Commencez à discuter pour finaliser la vente.",
        buyerChatMsg: "Vous pouvez maintenant contacter le vendeur pour finaliser l'accord.",
        startChat: "💬 Démarrer le chat",
        sellerAwaitingFee: "⏳ En attente du paiement des frais de chat par l'acheteur.",
        buyerAwaitingFee: "⏳ Le vendeur a accepté votre offre. Payez les frais de chat pour déverrouiller la conversation.",
        processing: "Traitement...",
        payFeeButton: "💰 Payer {{amount}} TND",
        optionalResponsePlaceholder: "Message de réponse facultatif...",
        accept: "✅ Accepter",
        reject: "❌ Refuser",
        buyerNotifiedFee: "L'acheteur a été notifié de payer les frais de chat. Vous serez informé une fois terminé.",
        withdrawOffer: "🚫 Retirer l'offre",
        acceptOfferTitle: "Accepter l'offre et déverrouiller le chat",
        acceptOfferDesc: "Confirmez l'acceptation et choisissez la durée d'accès de l'acheteur avant l'expiration de l'offre.",
        productLabel: "Produit",
        offerAmountLabel: "Montant de l'offre",
        chatFeeLabel: "Frais de chat",
        foreverOption: "Pour toujours (sans expiration)",
        foreverDesc: "L'offre reste active jusqu'à ce que vous la fermiez manuellement.",
        countdownOption: "Compte à rebours",
        countdownDesc: "Expire après le nombre d'heures sélectionné (max 720).",
        hours: "heures",
        provideDuration: "Indiquez une durée ou passez à pour toujours.",
        maxDuration: "La durée maximale autorisée est de 720 heures (30 jours).",
        confirmAcceptance: "Confirmer l'acceptation"
      },

      // Rooms Bid page
      roomsBid: {
        title: "Salles d'enchères",
        subtitle: "Choisissez de participer et d'enchérir avec vos jetons, ou observez l'action en direct gratuitement.",
        free: "Gratuit",
        noDeletePermission: "Vous n'avez pas la permission de supprimer des salles.",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette salle d'enchères ?",
        deleteSuccess: "Salle d'enchères supprimée avec succès",
        deleteFailed: "Échec de la suppression de la salle d'enchères",
        deleteError: "Erreur lors de la suppression de la salle d'enchères",
        untitledRoom: "Salle sans titre",
        live: "En direct",
        scheduled: "Planifiée",
        descriptionComingSoon: "Description à venir.",
        lastBid: "Dernière enchère",
        timeRemaining: "Temps restant",
        participationFee: "Frais de participation : {{fee}}",
        participate: "Participer",
        signInToParticipate: "Se connecter pour participer",
        view: "Voir",
        startingBid: "Mise de départ",
        startsIn: "Démarre dans",
        notYetAvailable: "Pas encore disponible",
        notStartedTooltip: "Cette salle d'enchères n'a pas encore commencé",
        comingSoon: "Bientôt",
        createRoom: "Créer une salle",
        activeAuctions: "Enchères actives",
        upcomingRooms: "Salles à venir",
        noRoomsTitle: "Aucune salle d'enchères disponible",
        noRoomsDesc: "Il n'y a pas de salles en direct ou planifiées pour le moment. Revenez bientôt ou créez une nouvelle salle.",
        autoRefreshNotice: "Restez connecté\u2014les salles se rafraîchissent automatiquement toutes les 30 secondes.",
        guestBanner: {
          title: "Aperçu public des enchères",
          description: "Parcourez librement les salles en direct et planifiées. Connectez-vous lorsque vous souhaitez participer ou enchérir.",
          action: "Se connecter"
        }
      },

      // Auction Room Management page
      auctionManagement: {
        accessDenied: "Accès refusé",
        noPermission: "Vous n'avez pas la permission de gérer les salles d'enchères.",
        title: "Gestion des salles d'enchères",
        createRoom: "Créer une salle d'enchères",
        unknownProduct: "Produit inconnu",
        unknownUser: "Utilisateur inconnu",
        minBid: "Mise minimale :",
        bidIncrement: "Incrément :",
        currentBid: "Enchère actuelle :",
        noBid: "Aucune",
        participants: "Participants :",
        start: "Début :",
        end: "Fin :",
        mode: "Mode :",
        history: {
          title: "Historique des salles d'enchères",
          subtitle: "Consultez les salles clôturées ou annulées et inspectez les chronologies des enchères.",
          closed: "Clôturée",
          cancelled: "Annulée",
          recentRooms: "Salles récentes",
          noRooms: "Aucune salle trouvée pour ce statut.",
          participants: "participants",
          finalBid: "Enchère finale",
          noCategory: "Sans catégorie",
          finalPrice: "Prix final",
          startTime: "Heure de début",
          endTime: "Heure de fin",
          duration: "Durée",
          winner: "Gagnant",
          noWinner: "Aucun gagnant",
          participationFee: "Frais de participation",
          minBidIncrement: "Mise min. / Incrément",
          bidHistory: "Historique des enchères",
          refreshing: "Actualisation\u2026",
          bidder: "Enchérisseur",
          amount: "Montant",
          time: "Heure",
          auto: "Auto",
          noBids: "Aucune enchère n'a été enregistrée pour cette salle.",
          selectRoom: "Sélectionnez une salle pour voir son historique."
        },
        form: {
          editTitle: "Modifier la salle d'enchères",
          createTitle: "Créer une salle d'enchères",
          product: "Produit",
          selectProduct: "Sélectionner un produit",
          reservableOnly: "Seuls les produits réservables peuvent être utilisés pour créer des salles d'enchères.",
          feeApplied: "Frais de participation appliqués : {{fee}}",
          feeFromProduct: "Cette valeur provient directement de la configuration du produit.",
          participationFee: "Frais de participation (TND)",
          selectProductFirst: "Sélectionnez d'abord un produit",
          feeHelp: "Ces frais sont tirés directement du produit sélectionné. Modifiez le produit pour les changer.",
          minimumBid: "Mise minimale (TND)",
          bidIncrement: "Incrément (TND)",
          startTime: "Heure de début",
          durationMinutes: "Durée (minutes)",
          computedEndTime: "Heure de fin calculée",
          packIncrement: "Incrément de pack (enchères par pack)",
          derivedServerSide: "Calculé côté serveur",
          mode: "Mode",
          fixed: "Fixe",
          autoExtend: "Extension automatique",
          status: "Statut",
          pending: "En attente",
          active: "Actif",
          closed: "Clôturé",
          cancelled: "Annulé",
          autoExtendDuration: "Durée d'extension automatique (secondes)",
          triggerThreshold: "Seuil de déclenchement (secondes restantes)",
          update: "Mettre à jour la salle",
          create: "Créer la salle"
        },
        errors: {
          startTimeRequired: "L'heure de début et la durée sont requises",
          durationPositive: "La durée doit être un nombre positif de minutes",
          endAfterStart: "L'heure de fin doit être postérieure à l'heure de début",
          saveFailed: "Erreur lors de l'enregistrement de la salle d'enchères",
          deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette salle d'enchères ?",
          deleteFailed: "Erreur lors de la suppression de la salle d'enchères"
        }
      },

      // Second Hand Rooms page
      roomsSecondHand: {
        title: "Salles d'enchères d'occasion",
        subtitle: "Découvrez des trésors d'occasion dans des salles d'enchères sélectionnées. Suivez la disponibilité, les prix de réserve et contactez les vendeurs directement avec des offres sécurisées.",
        requestRoom: "Demander une salle d'enchères",
        status: {
          active: "En direct",
          pending: "En attente d'approbation",
          expired: "Expiré",
          sold: "Vendu",
          closed: "Fermé",
          canceled: "Fermé",
          scheduled: "Planifié"
        },
        condition: {
          like_new: "Comme neuf",
          excellent: "Excellent",
          good: "Bon",
          used: "Utilisé",
          fair: "Correct",
          poor: "Mauvais"
        },
        duration: {
          dayLeft: "1 jour restant",
          daysLeft: "{{count}} jours restants",
          daysHoursLeft: "{{days}}j {{hours}}h restants",
          hoursMinutesLeft: "{{hours}}h {{minutes}}min restantes"
        },
        card: {
          unnamed: "Produit sans nom",
          general: "Général",
          noDescription: "Aucune description fournie.",
          reservePrice: "Prix demandé",
          openToOffers: "Ouvert aux offres",
          duration: "Durée",
          condition: "État",
          seller: "Vendeur",
          anonymous: "Anonyme",
          makeOffer: "Faire une offre"
        },
        empty: {
          title: "Aucun article ne correspond à ce filtre",
          subtitle: "Revenez bientôt ; de nouvelles demandes arrivent toutes les quelques minutes une fois que les vendeurs soumettent leurs salles."
        },
        sellerForm: {
          title: "Demander une salle d'enchères",
          reviewNotice: "Votre demande est examinée par un administrateur. Une fois approuvée, la salle d'enchères sera mise en ligne selon les règles de durée que vous définissez ci-dessous.",
          tndNotice: "Les garanties TND protègent les deux parties. Consultez les règles avant de soumettre votre annonce.",
          viewRules: "Voir les règles de garantie TND",
          productName: "Nom du produit",
          category: "Catégorie",
          selectCategory: "Sélectionner une catégorie",
          description: "Description",
          reservePrice: "Prix demandé (TND)",
          reservePriceHelp: "Facultatif — laissez vide pour recevoir toute offre",
          condition: "État",
          uploadImages: "Télécharger des images",
          uploadHelp: "Téléchargez jusqu'à 5 images (10 Mo max chacune).",
          filesSelected: "{{count}} fichier(s) sélectionné(s)",
          listingVisibility: "Visibilité de l'annonce",
          listingVisibilityHelp: "Choisissez la durée de validité des offres acceptées par défaut. Sélectionner \"Infini\" masque la durée sur les annonces.",
          infinite: "Infini (\u221e)",
          countdown: "Compte à rebours",
          defaultDuration: "Durée par défaut (heures)",
          maxDuration: "Maximum de 720 heures (30 jours).",
          submitting: "Envoi en cours\u2026",
          submitRequest: "Soumettre la demande"
        },
        offerModal: {
          title: "Faire une offre",
          reservePrice: "Prix de réserve : {{price}}",
          lockNotice: "Faire une offre verrouille temporairement 1 TND jusqu'à la réponse du vendeur.",
          viewRules: "Voir les règles de garantie TND",
          offerLabel: "Votre offre (TND)",
          submitting: "Envoi en cours\u2026",
          submitOffer: "Soumettre l'offre"
        },
        toasts: {
          loginRequired: "Veuillez vous connecter pour faire une offre.",
          ownProduct: "Vous ne pouvez pas faire une offre sur votre propre produit.",
          invalidAmount: "Veuillez entrer un montant d'offre valide.",
          offerSuccess: "Offre soumise avec succès ! Le vendeur examinera votre offre.",
          offerFailed: "Échec de la soumission de l'offre.",
          offerFailedRetry: "Échec de la soumission de l'offre. Veuillez réessayer.",
          fillRequired: "Veuillez remplir tous les champs obligatoires.",
          pricePositive: "Le prix de départ doit être supérieur à 0.",
          reserveMinimum: "Le prix de réserve doit être supérieur ou égal au prix de départ.",
          requestSuccess: "Demande de salle d'enchères soumise ! Un administrateur examinera votre demande.",
          requestFailed: "Échec de la soumission de la demande.",
          requestFailedRetry: "Échec de la soumission de la demande. Veuillez réessayer."
        }
      },

      // Payment page
      payment: {
        noProductId: "Aucun identifiant de produit fourni",
        productNotFoundTitle: "Produit introuvable",
        invalidResponse: "Format de réponse invalide",
        nameRequired: "Le nom est requis",
        emailRequired: "L'email est requis",
        cardRequired: "Le numéro de carte est requis",
        expiryRequired: "La date d'expiration est requise",
        cvcRequired: "Le code CVC est requis",
        addressRequired: "L'adresse est requise",
        cityRequired: "La ville est requise",
        postalCodeRequired: "Le code postal est requis",
        invalidCardLength: "Longueur du numéro de carte invalide",
        invalidExpiryFormat: "Format d'expiration invalide (MM/AA)",
        invalidCvcLength: "Longueur du code CVC invalide",
        loginRequired: "Veuillez vous connecter pour finaliser l'achat.",
        fixErrors: "Veuillez corriger les erreurs suivantes :",
        purchaseFailed: "Échec de l'achat",
        paymentSuccessRedirect: "Paiement réussi ! La commande de {{name}} a été traitée.",
        unexpectedResponse: "Réponse inattendue du serveur",
        paymentFailed: "Échec du paiement. Veuillez réessayer.",
        loadingProduct: "Chargement des détails du produit...",
        backToProducts: "Retour aux produits",
        paymentSuccess: "Paiement réussi !",
        orderProcessed: "Votre commande pour {{name}} a été traitée.",
        totalPaid: "Total payé",
        quantity: "Quantité",
        redirecting: "Redirection vers la page des produits...",
        orderSummary: "Récapitulatif de commande",
        soldOut: "Épuisé",
        subtotal: "Sous-total",
        shipping: "Livraison",
        free: "Gratuit",
        tax: "Taxe (20%)",
        total: "Total",
        paymentInfo: "Informations de paiement",
        creditCardDetails: "Détails de la carte de crédit",
        fullName: "Nom complet",
        cardNumber: "Numéro de carte",
        expiryDate: "Date d'expiration",
        cvc: "CVC",
        billingAddress: "Adresse de facturation",
        address: "Adresse",
        city: "Ville",
        postalCode: "Code postal",
        payAmount: "Payer {{amount}}",
        purchaseWithWallet: "Acheter avec {{wallet}}",
        recoveryWallet: "Portefeuille de récupération",
        mainWallet: "Portefeuille principal",
        completePurchaseAuto: "Finaliser l'achat (optimisé automatiquement)"
      },

      // Theme
      darkMode: "Mode sombre",
      lightMode: "Mode clair",
      
      // Language
      language: "Langue",
      english: "English",
      arabic: "العربية",
      french: "Français"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    
    interpolation: {
      escapeValue: false // React already does escaping
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

export default i18n;
